import {
  CalcIndividualConfig,
  FindFiltersParams,
  PriceCorridor,
  ProductPriority,
  PromoIndividualConfig,
  PromotionIndividualConfig, Promotions,
  PromoType,
  RevaluationCalculateParams,
  SaleIndividualConfig,
  SelectBox,
  SelectMarket,
  SkuRotationPeriodType,
  StrategyOfPriceOpt,
  WatchEventsType,
} from '@/interfaces/global-data';
import {IModuleItemsProduct} from "@/pages/ModuleItems/data";
import { i18n } from '@/services/i18n';

export type VlGapType = 'PERCENT' | 'RUBLES' | null;

/** Creates a SelectBox option whose label is resolved from i18n on each read (locale-aware). */
function selectBoxWithI18nLabel<T>(value: T, i18nKey: string): SelectBox<T> {
  const box: any = { value };

  Object.defineProperty(box, 'label', {
    get: () => i18n.t(i18nKey) as string,
    enumerable: true,
    configurable: true,
  });

  return box as SelectBox<T>;
}

// tslint:disable-next-line: interface-name
export interface XYZSpreadParams {
  x: number;
  y: number;
  z: number;
}

// tslint:disable-next-line: interface-name
export interface UVWSpreadParams {
  u: number;
  v: number;
  w: number;
}

// tslint:disable-next-line: interface-name
export interface IntervalOfUltimateElasticity {
  lower: number;
  upper: number;
}

export enum TaskInfoFilterType {
  all = 'all', group = 'group', format = 'format', region = 'region', buyer = 'buyer',
  cluster = 'cluster', category = 'category', subcategory = 'subcategory', p_segemnt = 'p_segemnt',
}

export enum ProductInfoResponseProperty {
  group = 'groups', category = 'categories', subcategory = 'subcategories',
  format = 'storeFormat', region = 'storeRegion', cluster = 'storeCluster',
}

type TaskInfoFilterTypeLabelType = {
  [x in TaskInfoFilterType]: string
}

const taskInfoFilterTypeI18nKeys: Record<TaskInfoFilterType, string> = {
  [TaskInfoFilterType.all]: 'serverData.taskInfoFilterTypes.all',
  [TaskInfoFilterType.group]: 'serverData.taskInfoFilterTypes.group',
  [TaskInfoFilterType.buyer]: 'serverData.taskInfoFilterTypes.buyer',
  [TaskInfoFilterType.category]: 'serverData.taskInfoFilterTypes.category',
  [TaskInfoFilterType.cluster]: 'serverData.taskInfoFilterTypes.cluster',
  [TaskInfoFilterType.format]: 'serverData.taskInfoFilterTypes.format',
  [TaskInfoFilterType.p_segemnt]: 'serverData.taskInfoFilterTypes.productSegment',
  [TaskInfoFilterType.region]: 'serverData.taskInfoFilterTypes.region',
  [TaskInfoFilterType.subcategory]: 'serverData.taskInfoFilterTypes.subcategory',
};

export function getTaskInfoFilterTypeLabel(type: TaskInfoFilterType): string {
  return i18n.t(taskInfoFilterTypeI18nKeys[type]) as string;
}

export enum CompetitorsPriceMethods {
  MIN_PRICE = "MIN_PRICE",
  AVG_PRICE = "AVG_PRICE",
  MAX_PRICE = "MAX_PRICE",
  WESTENDORP = "WESTENDORP",
  MEDIAN_PRICES = "MEDIAN_PRICES",
}

export enum KviCartChangePrices {
  ALL_PRICES = "ALL_PRICES",
  ALL_CART_PRICES = "ALL_CART_PRICES",
  CORRELATION_PRICES = "CORRELATION_PRICES",
}

export enum NewProductPriceStrategy {
  SKIMMING_STRATEGY = "SKIMMING_STRATEGY",
  PENETRATION_STRATEGY = "PENETRATION_STRATEGY",
  MINIMUM_PROFITABILITY = "MINIMUM_PROFITABILITY",
}

export enum CategoryOfSuppliers {
  ALL_SUPPLIERS = "ALL_SUPPLIERS",
  ONLINE_SUPPLIERS = "ONLINE_SUPPLIERS",
}

export enum LoadEventPeriods {
  DAY = "DAY",
  WEEK = "WEEK",
  MONTH = "MONTH",
}

// 1. Значения как enum-like объект
export const LoadEventTypeValues = {
  CHANGING_PURCHASE_PRICE: "CHANGING_PURCHASE_PRICE",
  CHANGING_COMPETITORS_PRICE: "CHANGING_COMPETITORS_PRICE",
  CHANGING_MRP: "CHANGING_MRP",
  ADDING_NEW_PRODUCT: "ADDING_NEW_PRODUCT",
  CHANGING_LINES: "CHANGING_LINES",
} as const;
// 2. Тип на основе ключей объекта
export type LoadEventType = keyof typeof LoadEventTypeValues;
const eventTypeI18nKeys: Record<LoadEventType, string> = {
  CHANGING_PURCHASE_PRICE: 'selectOptions.eventTypes.changingPurchasePrice',
  CHANGING_COMPETITORS_PRICE: 'selectOptions.eventTypes.changingCompetitorsPrice',
  CHANGING_MRP: 'selectOptions.eventTypes.changingMrp',
  ADDING_NEW_PRODUCT: 'selectOptions.eventTypes.addingNewProduct',
  CHANGING_LINES: 'selectOptions.eventTypes.changingLines',
};

export const LoadEventTypeLabels = new Proxy({} as Record<LoadEventType, string>, {
  get(_target, prop: string) {
    const key = eventTypeI18nKeys[prop as LoadEventType];

    return key ? i18n.t(key) as string : prop;
  },
});

export function getEventTypeOptions(): Array<{ value: LoadEventType; label: string }> {
  return (Object.keys(eventTypeI18nKeys) as LoadEventType[]).map((value) => ({
    value,
    label: i18n.t(eventTypeI18nKeys[value]) as string,
  }));
}

export function getEventTypeLoadType(): Record<LoadEventType, { value: LoadEventType; label: string }> {
  return (Object.keys(eventTypeI18nKeys) as LoadEventType[]).reduce(
    (acc, value) => {
      acc[value] = { value, label: i18n.t(eventTypeI18nKeys[value]) as string };
      return acc;
    },
    {} as Record<LoadEventType, { value: LoadEventType; label: string }>
  );
}

export const eventTypeOptions = new Proxy([] as Array<{ value: LoadEventType; label: string }>, {
  get(_target, prop) {
    const arr = getEventTypeOptions();

    return (arr as any)[prop];
  },
});

export const eventTypeLoadType = new Proxy(
  {} as Record<LoadEventType, { value: LoadEventType; label: string }>,
  {
    get(_target, prop: string) {
      const key = eventTypeI18nKeys[prop as LoadEventType];

      if (key) {
        return { value: prop as LoadEventType, label: i18n.t(key) as string };
      }

      return undefined;
    },
  }
);

export enum LoadEventChangingType {
  INCREASE = "INCREASE",
  DECREASE = "DECREASE",
  BOTH = "BOTH",
}

type CompetitorsPriceMethodsSb = {
  [x in CompetitorsPriceMethods]: SelectBox<CompetitorsPriceMethods>
};

type KviCartChangePricesSb = {
  [x in KviCartChangePrices]: SelectBox<KviCartChangePrices>
};

type NewProductPriceStrategySb = {
  [x in NewProductPriceStrategy]: SelectBox<NewProductPriceStrategy>
};

type CategoryOfSuppliersSb = {
  [x in CategoryOfSuppliers]: SelectBox<CategoryOfSuppliers>
};

type EventPeriodicityType = {
  [x in LoadEventPeriods]: SelectBox<LoadEventPeriods>
};

type EventTypeChangingType = {
  [x in LoadEventChangingType]: SelectBox<LoadEventChangingType>
};

type PercentileTypeSb = { [x in PercentileTypes]: SelectBox<PercentileTypes> };

export const competitorsPriceMethodsSb: CompetitorsPriceMethodsSb = {
  MIN_PRICE: selectBoxWithI18nLabel(CompetitorsPriceMethods.MIN_PRICE, 'selectOptions.competitorsPriceMethods.minPrice'),
  AVG_PRICE: selectBoxWithI18nLabel(CompetitorsPriceMethods.AVG_PRICE, 'selectOptions.competitorsPriceMethods.avgPrice'),
  MAX_PRICE: selectBoxWithI18nLabel(CompetitorsPriceMethods.MAX_PRICE, 'selectOptions.competitorsPriceMethods.maxPrice'),
  WESTENDORP: selectBoxWithI18nLabel(CompetitorsPriceMethods.WESTENDORP, 'selectOptions.competitorsPriceMethods.westendorp'),
  MEDIAN_PRICES: selectBoxWithI18nLabel(CompetitorsPriceMethods.MEDIAN_PRICES, 'selectOptions.competitorsPriceMethods.medianPrices'),
};

export const competitorsPromoPriceMethodsSb: CompetitorsPriceMethodsSb = {
  MIN_PRICE: selectBoxWithI18nLabel(CompetitorsPriceMethods.MIN_PRICE, 'selectOptions.competitorsPromoPriceMethods.minPrice'),
  AVG_PRICE: selectBoxWithI18nLabel(CompetitorsPriceMethods.AVG_PRICE, 'selectOptions.competitorsPromoPriceMethods.avgPrice'),
  MAX_PRICE: selectBoxWithI18nLabel(CompetitorsPriceMethods.MAX_PRICE, 'selectOptions.competitorsPromoPriceMethods.maxPrice'),
  WESTENDORP: selectBoxWithI18nLabel(CompetitorsPriceMethods.WESTENDORP, 'selectOptions.competitorsPromoPriceMethods.westendorp'),
  MEDIAN_PRICES: selectBoxWithI18nLabel(CompetitorsPriceMethods.MEDIAN_PRICES, 'selectOptions.competitorsPromoPriceMethods.medianPrices'),
};

export const kviCartChangePricesSb: KviCartChangePricesSb = {
  ALL_PRICES: selectBoxWithI18nLabel(KviCartChangePrices.ALL_PRICES, 'selectOptions.kviCartChangePrices.allPrices'),
  ALL_CART_PRICES: selectBoxWithI18nLabel(KviCartChangePrices.ALL_CART_PRICES, 'selectOptions.kviCartChangePrices.allCartPrices'),
  CORRELATION_PRICES: selectBoxWithI18nLabel(KviCartChangePrices.CORRELATION_PRICES, 'selectOptions.kviCartChangePrices.correlationPrices'),
};

export const newProductPriceStrategySb: NewProductPriceStrategySb = {
  SKIMMING_STRATEGY: selectBoxWithI18nLabel(NewProductPriceStrategy.SKIMMING_STRATEGY, 'selectOptions.newProductPriceStrategy.skimmingStrategy'),
  PENETRATION_STRATEGY: selectBoxWithI18nLabel(NewProductPriceStrategy.PENETRATION_STRATEGY, 'selectOptions.newProductPriceStrategy.penetrationStrategy'),
  MINIMUM_PROFITABILITY: selectBoxWithI18nLabel(NewProductPriceStrategy.MINIMUM_PROFITABILITY, 'selectOptions.newProductPriceStrategy.minimumProfitability'),
};

export const categoryOfSuppliersSb: CategoryOfSuppliersSb = {
  ALL_SUPPLIERS: selectBoxWithI18nLabel(CategoryOfSuppliers.ALL_SUPPLIERS, 'selectOptions.categoryOfSuppliers.allSuppliers'),
  ONLINE_SUPPLIERS: selectBoxWithI18nLabel(CategoryOfSuppliers.ONLINE_SUPPLIERS, 'selectOptions.categoryOfSuppliers.onlineSuppliers'),
};

export const eventPeriodicityType: EventPeriodicityType = {
  DAY: selectBoxWithI18nLabel(LoadEventPeriods.DAY, 'selectOptions.periods.day'),
  MONTH: selectBoxWithI18nLabel(LoadEventPeriods.MONTH, 'selectOptions.periods.month'),
  WEEK: selectBoxWithI18nLabel(LoadEventPeriods.WEEK, 'selectOptions.periods.week'),
};

export const eventChangingTypeType: EventTypeChangingType = {
  BOTH: selectBoxWithI18nLabel(LoadEventChangingType.BOTH, 'selectOptions.changingType.both'),
  DECREASE: selectBoxWithI18nLabel(LoadEventChangingType.DECREASE, 'selectOptions.changingType.decrease'),
  INCREASE: selectBoxWithI18nLabel(LoadEventChangingType.INCREASE, 'selectOptions.changingType.increase'),
};

export enum CompetitorsPriceStrategies {
  EQUALS = "EQUALS",
  GREATER_THAN = "GREATER_THAN",
  GREATER_THAN_BUT_BORDER = "GREATER_THAN_BUT_BORDER",
  LESS_THAN = "LESS_THAN",
}

type CompetitorsPriceStrategiesSb = {
  [x in CompetitorsPriceStrategies]: SelectBox<CompetitorsPriceStrategies>
};

export const competitorsPriceStrategiesSb: CompetitorsPriceStrategiesSb = {
  EQUALS: selectBoxWithI18nLabel(CompetitorsPriceStrategies.EQUALS, 'selectOptions.competitorsPriceStrategies.equals'),
  GREATER_THAN: selectBoxWithI18nLabel(CompetitorsPriceStrategies.GREATER_THAN, 'selectOptions.competitorsPriceStrategies.greaterThan'),
  GREATER_THAN_BUT_BORDER: selectBoxWithI18nLabel(CompetitorsPriceStrategies.GREATER_THAN_BUT_BORDER, 'selectOptions.competitorsPriceStrategies.greaterThanButBorder'),
  LESS_THAN: selectBoxWithI18nLabel(CompetitorsPriceStrategies.LESS_THAN, 'selectOptions.competitorsPriceStrategies.lessThan'),
};

export const competitorsPromoPriceStrategiesSb: CompetitorsPriceStrategiesSb = {
  EQUALS: selectBoxWithI18nLabel(CompetitorsPriceStrategies.EQUALS, 'selectOptions.competitorsPromoPriceStrategies.equals'),
  GREATER_THAN: selectBoxWithI18nLabel(CompetitorsPriceStrategies.GREATER_THAN, 'selectOptions.competitorsPromoPriceStrategies.greaterThan'),
  GREATER_THAN_BUT_BORDER: selectBoxWithI18nLabel(CompetitorsPriceStrategies.GREATER_THAN_BUT_BORDER, 'selectOptions.competitorsPromoPriceStrategies.greaterThanButBorder'),
  LESS_THAN: selectBoxWithI18nLabel(CompetitorsPriceStrategies.LESS_THAN, 'selectOptions.competitorsPromoPriceStrategies.lessThan'),
};

export enum CategoryStrategies {
  EMPTY = "EMPTY",
  TRAFFIC_GENERATOR = "TRAFFIC_GENERATOR",
  INCOME_GENERATOR = "INCOME_GENERATOR",
  REVENUE_GENERATOR = "REVENUE_GENERATOR",
  HYBRID = "HYBRID",
  DIFFERENTIAL = "DIFFERENTIAL",
}

// Auto Approval Enums and Interfaces
export enum ApprovalStatus {
  NOT_APPROVED = "NOT_APPROVED",
  APPROVED_MANUALLY = "APPROVED_MANUALLY",
  APPROVED_AUTOMATICALLY = "APPROVED_AUTOMATICALLY",
}

// modes for correcting price file import
export enum PriceImportMode {
  REGULAR = 'REGULAR',
  PROMO = 'PROMO',
}

// tslint:disable-next-line: interface-name
export interface AutoApprovalParams {
  useAutoApproval: boolean;
  
  // Изменение цены в % (диапазон)
  priceChangeFrom?: number; // от -100 до ∞
  priceChangeTo?: number; // от -100 до ∞
  
  // Изменение маржи в пп (диапазон)
  marginChangeFrom?: number; // от -∞ до 100
  marginChangeTo?: number; // от -∞ до 100
  
  // Рентабельность SKU в % (диапазон)
  profitabilityFrom?: number; // от -∞ до 100
  profitabilityTo?: number; // от -∞ до 100
  
  // Максимальная рентабельность SKU в % (диапазон)
  maxProfitabilityFrom?: number; // от 0 до 100
  maxProfitabilityTo?: number; // от 0 до 100
  
  // PI в % (диапазон) - только для товаров KVI и КЦО
  piFrom?: number; // от 0 до ∞
  piTo?: number; // от 0 до ∞
  
  // Учет стоимости смены ценников
  revaluationEfficiencyCheck: boolean;
}

// Partial Rejection Note Enums and Interfaces
export enum PartialRejectionReason {
  NEW_VERSION = "NEW_VERSION",
  VALIDATION_ERROR = "VALIDATION_ERROR",
  SYSTEM_ERROR = "SYSTEM_ERROR",
  USER_ACTION = "USER_ACTION",
}

// tslint:disable-next-line: interface-name
export interface PartialRejectionNoteData {
  id: string;
  reason: PartialRejectionReason;
  rejectionDate: Date;
  message?: string;
  additionalInfo?: string;
  taskId?: number;
  reportId?: number;
}

// tslint:disable-next-line: interface-name
export interface TaskWithNotes {
  taskId: number;
  hasNotes: boolean;
  noteCount: number;
  latestNoteDate?: Date;
}

type CategoryStrategiesSb = {
  [x in CategoryStrategies]: SelectBox<CategoryStrategies>
};

type CategoryStrategiesSbShortType = { [x in CategoryStrategies]: string };

export const categoryStrategiesSet: CategoryStrategiesSb = {
  EMPTY: selectBoxWithI18nLabel(CategoryStrategies.EMPTY, 'selectOptions.categoryStrategies.empty'),
  TRAFFIC_GENERATOR: selectBoxWithI18nLabel(CategoryStrategies.TRAFFIC_GENERATOR, 'selectOptions.categoryStrategies.trafficGenerator'),
  INCOME_GENERATOR: selectBoxWithI18nLabel(CategoryStrategies.INCOME_GENERATOR, 'selectOptions.categoryStrategies.incomeGenerator'),
  REVENUE_GENERATOR: selectBoxWithI18nLabel(CategoryStrategies.REVENUE_GENERATOR, 'selectOptions.categoryStrategies.revenueGenerator'),
  HYBRID: selectBoxWithI18nLabel(CategoryStrategies.HYBRID, 'selectOptions.categoryStrategies.hybrid'),
  DIFFERENTIAL: selectBoxWithI18nLabel(CategoryStrategies.DIFFERENTIAL, 'selectOptions.categoryStrategies.differential'),
};

export function getCategoryStrategyShortLabel(strategy: string): string {
  const keyMap: Record<string, string> = {
    EMPTY: 'selectOptions.categoryStrategiesShort.empty',
    TRAFFIC_GENERATOR: 'selectOptions.categoryStrategiesShort.trafficGenerator',
    INCOME_GENERATOR: 'selectOptions.categoryStrategiesShort.incomeGenerator',
    REVENUE_GENERATOR: 'selectOptions.categoryStrategiesShort.revenueGenerator',
    HYBRID: 'selectOptions.categoryStrategiesShort.hybrid',
    DIFFERENTIAL: 'selectOptions.categoryStrategiesShort.differential',
  };
  return keyMap[strategy] ? i18n.t(keyMap[strategy]) as string : strategy;
}

export const categoryStrategiesSetShort = new Proxy({} as CategoryStrategiesSbShortType, {
  get(_target, prop: string) {
    return getCategoryStrategyShortLabel(prop);
  },
});

export enum KviStrategies {
  EQUALS = "EQUALS",
  GREATER_THAN = "GREATER_THAN",
  GREATER_THAN_BUT_BORDER = "GREATER_THAN_BUT_BORDER",
  LESS_THAN = "LESS_THAN",
}

type KviStrategiesSb = { [x in KviStrategies]: SelectBox<KviStrategies> };

export type CalcIndividualConfigMap = { [x in string]: CalcIndividualConfig };
export type PromoIndividualConfigMap = { [x in string]: PromoIndividualConfig };

export type SaleIndividualConfigMap = {
  [x in string]: SaleIndividualConfig;
};

export type PromotionIndividualConfigMap = {
  [x in string]: PromotionIndividualConfig;
};

export type SaleIndividualConfigFieldsToSave = {
  saleId: number | null,
  tradingId: number | null,
  groups: number[],
  categories: number[],
  subcategories: number[] | null[],
  storeFormat: number | null,
  storeRegions: number[],
  storeClusters: number[],
  products: any[],
};

export type ModuleItemIndividualConfigFieldsToSave = {
  saleId?: number | null,
  promotionId?: number | null,
  tradingId: number | null,
  groups: number[],
  categories: number[],
  subcategories: number[] | null[],
  storeFormat: number | null,
  storeRegions: number[],
  storeClusters: number[],
  products: IModuleItemsProduct[],
};

export type PromotionIndividualConfigFieldsToSave = {
  saleId: number | null,
  tradingId: number | null,
  groups: number[],
  categories: number[],
  subcategories: number[] | null[],
  storeFormat: number | null,
  storeRegions: number[],
  storeClusters: number[],
  products: any[],
};

export const kviStrategiesSet: KviStrategiesSb = {
  EQUALS: selectBoxWithI18nLabel(KviStrategies.EQUALS, 'selectOptions.kviStrategies.equals'),
  GREATER_THAN: selectBoxWithI18nLabel(KviStrategies.GREATER_THAN, 'selectOptions.kviStrategies.greaterThan'),
  GREATER_THAN_BUT_BORDER: selectBoxWithI18nLabel(KviStrategies.GREATER_THAN_BUT_BORDER, 'selectOptions.kviStrategies.greaterThanButBorder'),
  LESS_THAN: selectBoxWithI18nLabel(KviStrategies.LESS_THAN, 'selectOptions.kviStrategies.lessThan'),
};

// tslint:disable-next-line: interface-name
export interface GetTaskInfo {
  tradingId: number;
  type: TaskInfoFilterType;
  groups?: number[] | null;
  categories?: number[] | null;
  subcategories?: number[] | null;
  subcategory?: number[] | null;
  productSegments?: number[] | null;
  segments?: number[] | null;
  storeFormat: number[] | null;
  storeRegion: number[] | null;
  storeCluster: number[] | null;
  byReports?: boolean;
  rulesListCondition?: boolean;
}

// tslint:disable-next-line: interface-name
export interface PageParams {
  page: number;
  size: number;
}

// tslint:disable-next-line: interface-name
export interface FilesPageParams extends PageParams {
  type: FileType;
  tradingId?: number;
  fileNameFilterText?: string;
  fileUploadDateFilterValue?: string;
  userNameFilterValue?: string;
}

// tslint:disable-next-line: interface-name
export interface FileTemplateParams {
  type: FileType;
}

// tslint:disable-next-line: interface-name
export interface BaseFilter {
  category: number | null;
  storeCluster: number[] | null | number;
  storeClusters?: number[];
  seasonValues: number[] | null;
  buyerSegments: number[] | null;
  storeRegionValues: number[] | null;
  groupsOfProduct: number[] | null;
  categories: number[] | null;
  storeFormat: number | null;
  storeFormats?: number[];
  tradingId: number;
  calculationDepth: number; // calc+
  calculationDay: number; // calc+
  groupOfProduct: number | null; // calc+
  subcategories: number[] | null;
  productSegments: number[] | null;
  autoApprovalParams?: AutoApprovalParams; // Параметры автосогласования
}

// tslint:disable-next-line: interface-name
export interface BaseFilterRounding {
  /*selectMarket: SelectMarket;
  regionSelected: number[];
  buyerSegmentsList: number[];
  seasonSelected: number[];
  categoryOfProduct: number | null;
  formatSelected: number | null;*/
  category: number | null;
  storeCluster: number | null;
  seasonValues: number[] | null;
  buyerSegments: number[] | null;
  storeRegionValues: number[] | null;
  categoryStrategy?: CategoryStrategies;
  storeFormat: number | null;
  tradingId: number;
  groupOfProduct: number | null;
  subcategories: number[] | null;
  productSegments: number[] | null;
}

// tslint:disable-next-line: interface-name
export interface BaseFilterRoundingTable extends BaseFilterRounding {
  user: string;
  uploadDate: string;
}

// tslint:disable-next-line: interface-name
export interface RoundingRulesFilter {
  tradingId?: number | null;
  dateBefore?: string | null;
  dateAfter?: string | null;
  userFio?: string | null;
  storeFormat?: number | null;
  storeRegions?: number | null;
  storeCluster?: number | null;
  group?: number | null;
  category?: number | null;
  subcategories?: number | null;
  filterTypes?: string[] | null;
}

export interface KviRoundingRulesFilter {
  tradingId: number | null;
  kviCartId: number | null;
  dateBefore?: string | null;
  dateAfter?: string | null;
  userFio?: string | null;
}

export interface KviRoundingParams {
  tradingId: number | null;
  kviCartId: number | null;
  allNewPricesWithoutPennies: boolean;
  pennies: boolean;
  randomPennies: boolean;
  roundKviCompetitorPrice: boolean;
  roundPricesWithEqualCompStrategy: boolean;
  bigDigit: number | null;
  manualPennies: number | null;
}

export interface KviRoundingFindParams {
  tradingId: number | null;
  kviCartId: number | null;
}

// tslint:disable-next-line: interface-name
export interface BaseFilterRoundingRules extends BaseFilterRounding {
  allNewPricesWithoutPennies: boolean;
  filterType: string;
  pennies: boolean;
  randomPennies: boolean;
  roundKviCompetitorPrice: boolean;
  roundPricesWithEqualCompStrategy: boolean;
  bigDigit: number | null;
  manualPennies: number | null;
}

// tslint:disable-next-line: interface-name
export interface AllFiltersForRulesTable {
  categories: ProductTag[];
  groupsOfProduct: ProductTag[];
  subcategories: ProductTag[];
  storeFormat: ProductTag[];
  storeRegionValues: ProductTag[];
  storeCluster: ProductTag[];
}

export enum FileType {
  BASE = "BASE",
  FIXED_PRICE = "FIXED_PRICE",
  COMPETITORS = "COMPETITORS",
  LASTPRICES = "LASTPRICES",
  PROMO = "PROMO",
  KVICARTS = "KVICARTS",
  RULES = "RULES",
  INDIVIDUALPARAMS = "INDIVIDUALPARAMS",
  OKRBCODES = "OKRBCODES",
  SALES = "SALES",
  PRODUCT_LINE_DIRECTORY = "PRODUCT_LINE_DIRECTORY",
}

// tslint:disable-next-line: interface-name
export interface SkuFilter extends BaseFilter {
  skuFilter: string;
  promoFilter?: PromoFilterType;
  intervalOfUltimateElasticity?: IntervalOfUltimateElasticity;
  discountPrise: boolean;
  promo: boolean;
  sortBySKU?: number;
  kvi?: number;
  kviRecommendation?: number;
  ctm?: string | null;
  priceSegments?: Segment[] | null;
  productSegments: number[] | null;
  priceCorridor?: PriceCorridor | null;
  changePriceCorridor?: boolean;
  productsFilter?: PromoFilterProducts;
  internalCodes?: string[] | null;
  internalCodeFilterText?: string;
  recommendsInternalCodes?: string[] | null;
  needCalculateSku?: boolean;
  needCalculateSkuLastPricesOnly?: boolean;
  needCalculateSkuActualProfitabilityOnly?: boolean;
  keyCharacteristicOfTheProductIsOne?: string[] | null;
  keyCharacteristicOfTheProductIsTwo?: string[] | null;
  keyCharacteristicOfTheProductIsThree?: string[] | null;
  keyCharacteristicOfTheProductIsFour?: string[] | null;
  keyCharacteristicOfTheProductIsFive?: string[] | null;
  recipeAttributes?: string[] | null;
  treatmentAttributes?: number[] | null;
  alwaysCalculateProductSegments?: boolean;
  countAtLastPurchasePrice?: boolean;
  productRangeFilter?: string;
  correlation?: number;
  searchPromoRecommends?: boolean;
  calcRecommendsWithPriceSegments?: boolean;
  tagType?: TaskInfoFilterType | null;
  groupFilterInSkuList?: number;
  categoryFilterInSkuList?: number;
  subcategoryFilterInSkuList?: number;
  segmentFilterInSkuList?: number;
  countAtMaxPurchasePrice?: boolean;
  storeFormats?: number[];
  storeClusters?: number[];
}

// tslint:disable-next-line: interface-name
export interface CompetitorsPriceFilter extends SkuFilter {
  competitorsPricePeriod: number;
  sortingField: SortingField | null;
  isKviCartCompetitorsGrid: boolean;
  competitorCodes?: string[];
  skuNames?: string[];
  competitors?: string[];
  competitorPriorities?: number[];
  lastPrices?: number[];
  promoPrices?: number[];
  competitorPrices?: number[];
  keyCharacteristicsOfTheProductIsOnes?: string[];
  keyCharacteristicsOfTheProductIsTwos?: string[];
  priceDates?: number[];
  deltas?: number[];
}

export interface ICompetitorPricesFilterOptions {
  optionCompetitorPrices: number[];
  optionCompetitorPriorities: number[];
  optionCompetitorsCodes: string[];
  optionCompetitorsNames: string[];
  optionDeltas: number[];
  optionInternalCodes: string[];
  optionKeyCharacteristicsOfTheProductIsOne: string[];
  optionKeyCharacteristicsOfTheProductIsTwo: string[];
  optionLastPrices: number[];
  optionPriceDates: number[];
  optionPromoPrices: number[];
  optionRegions: string[];
  optionSkuNames: string[];
  optionStoreFormats: string[];
  kviOptions: Array<SelectBox<number>>;
  ctmOptions: Array<SelectBox<string>>;
}

// tslint:disable-next-line: interface-name
export interface KviCartsSkuFilter extends CompetitorsPriceFilter {
  kviCartIdFilter?: number;
  kviCartNameFilter?: string;
  internalCodesAndSkuNames?: InternalCodeAndSkuName[];
  kviCartProductIds?: number[];
}

// tslint:disable-next-line: interface-name
export interface CompetitorPrice {
  name: string;
  price: number;
}

// tslint:disable-next-line: interface-name
export interface RegularCompetitorsPricesView {
  internalCode: string;
  skuName: string;
  lastPrice: number;
  promoPrice: number;
  kvi: number | null;
  ctm: string | null;
  keyCharacteristicOfTheProductIsOne: string;
  keyCharacteristicOfTheProductIsTwo: string;
  storeFormat: string;
  storeRegion: string;
  priceDate: Date;
  competitorName: string;
  competitorCode: string;
  competitorPrice: number;
  competitorPriority: number;
  delta: number;
}

// tslint:disable-next-line: interface-name
export interface CompetitorsPricesView {
  internalCode: string;
  skuName: string;
  lastPrice: number;
  promoPrice: number;
  minCompetitorPrice: CompetitorPrice | null;
  maxCompetitorPrice: CompetitorPrice | null;
  avgCompetitorPrices: number | null;
  lastPromoCompetitorPrice: number;
  minPromoCompetitorPrice: CompetitorPrice | null;
  maxPromoCompetitorPrice: CompetitorPrice | null;
  medianPromoCompetitorPrice: number | null;
  kvi: number | null;
  maxCompetitorsDate: Date;
  delta: number | null;
  ctm: string;
  keyCharacteristicOfTheProductIsOne: string;
  keyCharacteristicOfTheProductIsTwo: string;
  competitorName: string;
  competitorPriority: number;
  storeFormat: string;
  storeRegion: string;
}

// tslint:disable-next-line: interface-name
export interface KviShoppingCartFilters extends BaseFilter {
  allCheck: boolean; // +
  discountPrise: boolean; // +
  pennies: boolean; // +
  countAtLastPurchasePrice: boolean;
  countAtFuturePurchasePrice: boolean;
  allNewPricesWithoutPennies: boolean; // +
  xyzChecked: boolean; // +
  uvwChecked: boolean; // +
  allClaster: boolean; // +
  coefficientK1: number; // +
  coefficientK2: number; // +
  spreadUVWParams: UVWSpreadParams; // +
  spreadXYZParams: XYZSpreadParams; // +
  correlation: number; // +
  highElasticity: number; // +
  intervalOfUltimateElasticity: IntervalOfUltimateElasticity; // +
  competitorsPricesParams: number; // +
  kviStrategy: KviStrategies; // +
  period: number;
  periodPriceIncreaseLimit: number;
  percentageOfCompetitorsPrice: string; // +
  marginalProfitability: number; // +
  marginalProfit: number; // +
  marginalProfitKvi?: number; // +
  marginalProfitCompetitors?: number; // +
  maxMarginalProfit: number; // +
  marginalPriceDifference: number; // +
  newProductLifespanInDays: number; // +
  priceCorridor: PriceCorridor; // +
  changePriceCorridor: boolean; // +
  priceIncreaseLimit: number; // +
  priceDecreaseLimit?: number; // +
  kviPriceDecreaseLimit?: number; // +
  compPriceDecreaseLimit?: number; // +
  fixLowerBorder: boolean; // +
  individualCommonParams: CalcIndividualConfigMap;
  depthToPurchasePrice: number; // +
  priceFamilies: boolean; // +
  hardPriceFamilies: boolean; // +
  priceOpt: StrategyOfPriceOpt; // +
  useCompetitorsPrices: boolean; // +
  byPromoPrices: boolean; // +
  competitorsPricePeriod: number; // +
  competitorsCodes: string[]; // +
  competitorsPriceMethod: CompetitorsPriceMethods; // +
  competitorsPriceAdjustment: string; // +
  competitorsInfluencesCoef: number; // +
  competitorsMinimumProfit: number; // +
  competitorsMaximumProfit: number; // +
  competitorsControlParam: number; // +
  competitorsPriceStrategy: CompetitorsPriceStrategies; // +
  roundKviCompetitorPrice: boolean; // +
  calculateByRegression: boolean; // +
  recalculateCtm: boolean;
  priceChangeLimit: number;
  priceChangeLimitKvi: number;
  priceChangeLimitComp: number;
  useBrutForcingParams: boolean;
  optimizationDiff: number;
  saveAfterOptimize: boolean;
  calculatePYR: boolean;
  decreaseSalesCount?: number | null;
  newProductPriceStrategy: NewProductPriceStrategy;
  priceGap: number;
  priceGapCtmPc: number;
  findAutomaticallyPriceSegments: boolean;
  countPriceSegments: number;
  promoCalc: boolean;
  categoryStrategy?: CategoryStrategies;
  percentageOfRecommendsKviCount?: number | null;
  useSegmentsPriceInSearchKVIRecParam?: boolean | null;
  saveKviSign?: boolean | null;
  useCompetitorsForKviRecommends?: boolean | null;
  roundPricesWithEqualCompStrategy: boolean;
  competitorsPeriodForKviRecommends?: number | null;
  productCodesForAddKvi: string[];
  productCodesForDeleteKvi: string[];
  productCodesForAddCtmPurpose: string[];
  productCodesForDeleteCtmPurpose: string[];
  taskId?: number | null;
  calculateCompAllowedDeviation: boolean;
  useCompElasticity: boolean;
  minProfitKVI: number;
  maxProfitKVI: number;
  productRangesAndInternalCodes: InternalCodeAndProductRange[];
  randomPennies: boolean;
  manualPennies: number | null;
  forecastAccuracy?: boolean;
  countAtMaxPurchasePrice?: boolean;
  percentileType: PercentileTypes;
  useAllProductBatchNumbers?: boolean;
  // Номер партии добавляется не во все модули и расчеты
  calcWithBatchNumber: boolean;
  competitorPriceControlParameterPc: number;
  countPriceBySkuWithoutBatches?: boolean;
  countByAvgPurchasePrice?: boolean;
  calcPurchasePriceByClusters?: boolean;
  useEmptyRemains?: boolean;
  useArrivalDate?: boolean;
  thompsonSampling: boolean;
  priceExperiment?: boolean;
  priceExperimentSampleSize?: number;
  updateInfo?: UpdateInfo | null;
  backBasketPart?: number;
  revaluationCalculateParams: RevaluationCalculateParams;
  checkMarginDeclineProfit?: boolean;
  declineProfitabilityPc?: number;
  searchPreviousPricesDepth?: number;
  compensateLowerTargets?: boolean;
  isFixPricePromo: boolean;
  spreadByPriceSegments: boolean;
  // inflation?: boolean;
  kviCartIdFilter?: number;
}

// tslint:disable-next-line: interface-name
export interface CalcFilters extends BaseFilter {
  allCheck: boolean; // +
  discountPrise: boolean; // +
  // pennies: boolean; // +
  countAtLastPurchasePrice: boolean;
  countAtFuturePurchasePrice: boolean;
  // allNewPricesWithoutPennies: boolean; // +
  xyzChecked: boolean; // +
  uvwChecked: boolean; // +
  allClaster: boolean; // +
  coefficientK1: number; // +
  coefficientK2: number; // +
  spreadUVWParams: UVWSpreadParams; // +
  spreadXYZParams: XYZSpreadParams; // +
  correlation: number; // +
  highElasticity: number; // +
  intervalOfUltimateElasticity: IntervalOfUltimateElasticity; // +
  competitorsPricesParams: number; // +
  kviStrategy: KviStrategies; // +
  period: number;
  periodPriceIncreaseLimit: number;
  percentageOfCompetitorsPrice: string; // +
  marginalProfitability: number; // +
  marginalProfit: number; // +
  marginalProfitKvi?: number; // +
  marginalProfitCompetitors?: number; // +
  maxMarginalProfit: number; // +
  marginalPriceDifference: number; // +
  newProductLifespanInDays: number; // +
  priceCorridor: PriceCorridor; // +
  changePriceCorridor: boolean; // +
  priceIncreaseLimit: number; // +
  priceDecreaseLimit?: number; // +
  kviPriceDecreaseLimit?: number; // +
  compPriceDecreaseLimit?: number; // +
  fixLowerBorder: boolean; // +
  individualCommonParams: CalcIndividualConfigMap;
  depthToPurchasePrice: number; // +
  priceFamilies: boolean; // +
  hardPriceFamilies: boolean; // +
  priceOpt: StrategyOfPriceOpt; // +
  useCompetitorsPrices: boolean; // +
  byPromoPrices: boolean; // +
  competitorsPricePeriod: number; // +
  competitorsCodes: string[]; // +
  competitorsPriceMethod: CompetitorsPriceMethods; // +
  competitorsPriceAdjustment: string; // +
  competitorsInfluencesCoef: number; // +
  competitorsMinimumProfit: number; // +
  competitorsMaximumProfit: number; // +
  competitorsControlParam: number; // +
  competitorsPriceStrategy: CompetitorsPriceStrategies; // +
  daysToPriceChangeStart?: number | null;
  daysToPriceChangeFinish?: number | null;
  // roundKviCompetitorPrice: boolean; // +
  calculateByRegression: boolean; // +
  recalculateCtm: boolean;
  priceChangeLimit: number;
  priceChangeLimitKvi: number;
  priceChangeLimitComp: number;
  useBrutForcingParams: boolean;
  optimizationDiff: number;
  saveAfterOptimize: boolean;
  calculatePYR: boolean;
  decreaseSalesCount?: number | null;
  newProductPriceStrategy: NewProductPriceStrategy;
  priceGap: number;
  priceGapCtmPc: number;
  findAutomaticallyPriceSegments: boolean;
  countPriceSegments: number;
  promoCalc: boolean;
  categoryStrategy?: CategoryStrategies;
  percentageOfRecommendsKviCount?: number | null;
  useSegmentsPriceInSearchKVIRecParam?: boolean | null;
  saveKviSign?: boolean | null;
  useCompetitorsForKviRecommends?: boolean | null;
  roundPricesWithEqualCompStrategy: boolean;
  competitorsPeriodForKviRecommends?: number | null;
  productCodesForAddKvi: string[];
  productCodesForDeleteKvi: string[];
  productCodesForAddCtmPurpose: string[];
  productCodesForDeleteCtmPurpose: string[];
  taskId?: number | null;
  calculateCompAllowedDeviation: boolean;
  useCompElasticity: boolean;
  minProfitKVI: number;
  maxProfitKVI: number;
  productRangesAndInternalCodes: InternalCodeAndProductRange[];
  // randomPennies: boolean;
  // manualPennies: number | null;
  forecastAccuracy?: boolean;
  countAtMaxPurchasePrice?: boolean;
  percentileType: PercentileTypes;
  useAllProductBatchNumbers?: boolean;
  // Номер партии добавляется не во все модули и расчеты
  calcWithBatchNumber: boolean;
  competitorPriceControlParameterPc: number;
  countPriceBySkuWithoutBatches?: boolean;
  countByAvgPurchasePrice?: boolean;
  calcPurchasePriceByClusters?: boolean;
  useEmptyRemains?: boolean;
  useArrivalDate?: boolean;
  thompsonSampling: boolean;
  priceExperiment?: boolean;
  priceExperimentSampleSize?: number;
  priceExperimentDiscountRangeFrom?: number;
  priceExperimentDiscountRangeTo?: number;
  priceExperimentStep?: number;
  priceExperimentStartDate?: string | null;
  priceExperimentEndDate?: string | null;
  strategyValidityBeginDate?: number | null;
  strategyValidityEndDate?: number | null;
  updateInfo?: UpdateInfo | null;
  backBasketPart?: number;
  revaluationCalculateParams: RevaluationCalculateParams;
  checkMarginDeclineProfit?: boolean;
  declineProfitabilityPc?: number;
  searchPreviousPricesDepth?: number;
  compensateLowerTargets?: boolean;
  inflation?: boolean;
  calculateBySpeedSale?: boolean;
  applyOmnichannel?: boolean;
  purchasePriceSuppliers?: boolean;
  purchasePriceSuppliersWithRemains?: boolean;
  categoryOfSuppliers?: CategoryOfSuppliers;
  purchasePriceSuppliersMinProfit?: number;
  purchasePriceSuppliersMinProfitForKVI?: number;
  purchasePriceSuppliersMinProfitForCompetitors?: number;
  sendGeneratedReportNotification?: boolean;
  isFixPricePromo: boolean;
  spreadByPriceSegments: boolean;
  autoApprovalParams?: AutoApprovalParams;
}

// tslint:disable-next-line: interface-name
export interface PreCalcFilters extends BaseFilter {
  id: number;
  depthToPurchasePrice: number;
  calculateComparable: boolean;
  calculateActual: boolean;
  calculateAverage: boolean;
  calculatePurchasePrice: boolean;
  calculateRegularPrice: boolean;
  calculateCoeffElasticity: boolean;
  calculateSegment: boolean;
}

// tslint:disable-next-line: interface-name
export interface UpdateInfo {
  updateDate: string;
  updatedBy: string;
}

// tslint:disable-next-line: interface-name
export interface ScenarioAnalysisFilters extends CalcFilters {
  allCheck: boolean; // +
  discountPrise: boolean; // +
  pennies: boolean; // +
  countAtLastPurchasePrice: boolean;
  countAtFuturePurchasePrice: boolean;
  // allNewPricesWithoutPennies: boolean; // +
  xyzChecked: boolean; // +
  uvwChecked: boolean; // +
  allClaster: boolean; // +
  coefficientK1: number; // +
  coefficientK2: number; // +
  spreadUVWParams: UVWSpreadParams; // +
  spreadXYZParams: XYZSpreadParams; // +
  correlation: number; // +
  highElasticity: number; // +
  intervalOfUltimateElasticity: IntervalOfUltimateElasticity; // +
  competitorsPricesParams: number; // +
  kviStrategy: KviStrategies; // +
  period: number;
  periodPriceIncreaseLimit: number;
  percentageOfCompetitorsPrice: string; // +
  marginalProfitability: number; // +
  marginalProfit: number; // +
  marginalPriceDifference: number; // +
  newProductLifespanInDays: number; // +
  priceCorridor: PriceCorridor; // +
  changePriceCorridor: boolean; // +
  priceIncreaseLimit: number; // +
  fixLowerBorder: boolean; // +
  depthToPurchasePrice: number; // +
  priceFamilies: boolean; // +
  hardPriceFamilies: boolean; // +
  priceOpt: StrategyOfPriceOpt; // +
  useCompetitorsPrices: boolean; // +
  byPromoPrices: boolean; // +
  competitorsPricePeriod: number; // +
  competitorsCodes: string[]; // +
  competitorsPriceMethod: CompetitorsPriceMethods; // +
  competitorsPriceAdjustment: string; // +
  competitorsInfluencesCoef: number; // +
  competitorsMinimumProfit: number; // +
  competitorsControlParam: number; // +
  competitorsPriceStrategy: CompetitorsPriceStrategies; // +
  roundKviCompetitorPrice: boolean; // +
  calculateByRegression: boolean; // +
  recalculateCtm: boolean;
  priceChangeLimit: number;
  useBrutForcingParams: boolean;
  saveAfterOptimize: boolean;
  calculatePYR: boolean;
  decreaseSalesCount?: number | null;
  newProductPriceStrategy: NewProductPriceStrategy;
  priceGap: number;
  priceGapCtmPc: number;
  findAutomaticallyPriceSegments: boolean;
  countPriceSegments: number;
  promoCalc: boolean;
  categoryStrategy?: CategoryStrategies;
  roundPricesWithEqualCompStrategy: boolean;
  taskId?: number | null;
  calculateCompAllowedDeviation: boolean;
  useCompElasticity: boolean;
  minProfitKVI: number;
  maxProfitKVI: number;
  randomPennies: boolean;
  manualPennies: number | null;
  forecastAccuracy?: boolean;
  countAtMaxPurchasePrice?: boolean;
  percentileType: PercentileTypes;
  useAllProductBatchNumbers?: boolean;
  // Номер партии добавляется не во все модули и расчеты
  calcWithBatchNumber: boolean;
  competitorPriceControlParameterPc: number;
  purchasePriceSuppliers?: boolean;
  purchasePriceSuppliersWithRemains?: boolean;
  purchasePriceSuppliersMinProfit?: number;
  purchasePriceSuppliersMinProfitForKVI?: number;
  percentageOfRecommendsKviCount?: number | null;
  useSegmentsPriceInSearchKVIRecParam?: boolean | null;
  saveKviSign?: boolean | null;
  useCompetitorsForKviRecommends?: boolean | null;
  competitorsPeriodForKviRecommends?: number | null;
  useFixedPrices: boolean;
}

// tslint:disable-next-line: interface-name
export interface ResultsFilters extends CalcFilters {
  allCheck: boolean; // +
  discountPrise: boolean; // +
  pennies: boolean; // +
  countAtLastPurchasePrice: boolean;
  countAtFuturePurchasePrice: boolean;
  // allNewPricesWithoutPennies: boolean; // +
  xyzChecked: boolean; // +
  uvwChecked: boolean; // +
  allClaster: boolean; // +
  coefficientK1: number; // +
  coefficientK2: number; // +
  spreadUVWParams: UVWSpreadParams; // +
  spreadXYZParams: XYZSpreadParams; // +
  correlation: number; // +
  highElasticity: number; // +
  intervalOfUltimateElasticity: IntervalOfUltimateElasticity; // +
  competitorsPricesParams: number; // +
  kviStrategy: KviStrategies; // +
  period: number;
  periodPriceIncreaseLimit: number;
  percentageOfCompetitorsPrice: string; // +
  marginalProfitability: number; // +
  marginalProfit: number; // +
  marginalPriceDifference: number; // +
  newProductLifespanInDays: number; // +
  priceCorridor: PriceCorridor; // +
  changePriceCorridor: boolean; // +
  priceIncreaseLimit: number; // +
  fixLowerBorder: boolean; // +
  depthToPurchasePrice: number; // +
  priceFamilies: boolean; // +
  hardPriceFamilies: boolean; // +
  priceOpt: StrategyOfPriceOpt; // +
  useCompetitorsPrices: boolean; // +
  byPromoPrices: boolean; // +
  competitorsPricePeriod: number; // +
  competitorsCodes: string[]; // +
  competitorsPriceMethod: CompetitorsPriceMethods; // +
  competitorsPriceAdjustment: string; // +
  competitorsInfluencesCoef: number; // +
  competitorsMinimumProfit: number; // +
  competitorsControlParam: number; // +
  competitorsPriceStrategy: CompetitorsPriceStrategies; // +
  roundKviCompetitorPrice: boolean; // +
  calculateByRegression: boolean; // +
  recalculateCtm: boolean;
  priceChangeLimit: number;
  calculatePYR: boolean;
  decreaseSalesCount?: number | null;
  newProductPriceStrategy: NewProductPriceStrategy;
  priceGap: number;
  priceGapCtmPc: number;
  findAutomaticallyPriceSegments: boolean;
  countPriceSegments: number;
  promoCalc: boolean;
  categoryStrategy?: CategoryStrategies;
  percentageOfRecommendsKviCount?: number | null;
  useSegmentsPriceInSearchKVIRecParam?: boolean | null;
  saveKviSign?: boolean | null;
  useCompetitorsForKviRecommends?: boolean | null;
  roundPricesWithEqualCompStrategy: boolean;
  competitorsPeriodForKviRecommends?: number | null;
  taskId?: number | null;
  calculateCompAllowedDeviation: boolean;
  useCompElasticity: boolean;
  minProfitKVI: number;
  randomPennies: boolean;
  manualPennies: number | null;
  forecastAccuracy?: boolean;
  countAtMaxPurchasePrice?: boolean;
  percentileType: PercentileTypes;
  useAllProductBatchNumbers?: boolean;
  // Номер партии добавляется не во все модули и расчеты
  calcWithBatchNumber: boolean;
  competitorPriceControlParameterPc: number;
}

// tslint:disable-next-line: interface-name
export interface PackageParamsInDetailReportFilters {
  coefficientK1: number; // +
  coefficientK2: number; // +
  allCheck: boolean;
  discountPrise: boolean;
  pennies: boolean;
  randomPennies: boolean;
  countPriceBySkuWithoutBatches?: boolean;
  countByAvgPurchasePrice?: boolean;
  calcPurchasePriceByClusters?: boolean;
  useEmptyRemains: boolean;
  useArrivalDate: boolean;
  countAtLastPurchasePrice: boolean;
  countAtFuturePurchasePrice: boolean;
  allNewPricesWithoutPennies: boolean;
  allClaster: boolean;
  xyzChecked: boolean;
  uvwChecked: boolean;
  spreadUVWParams: UVWSpreadParams;
  spreadXYZParams: XYZSpreadParams;
  intervalOfUltimateElasticity: IntervalOfUltimateElasticity;
  calculationDepth: number;
  calculationDay: number;
  competitorsPricesParams: number;
  minProfitKVI: number;
  maxProfitKVI: number;
  percentageOfCompetitorsPrice: string;
  marginalProfitability: number;
  marginalProfit: number;
  maxMarginalProfit: number;
  marginalPriceDifference: number;
  newProductLifespanInDays?: number; // +
  priceIncreaseLimit: number;
  priceDecreaseLimit?: number;
  kviPriceDecreaseLimit?: number;
  compPriceDecreaseLimit?: number;
  correlation: number;
  highElasticity: number;
  kviStrategy: KviStrategies;
  period: number;
  periodPriceIncreaseLimit: number;
  priceCorridor: PriceCorridor;
  fixLowerBorder: boolean;
  depthToPurchasePrice: number;
  priceFamilies: boolean;
  hardPriceFamilies: boolean;
  changePriceCorridor: boolean;
  useCompetitorsPrices: boolean;
  byPromoPrices: boolean;
  roundPricesWithEqualCompStrategy: boolean;
  competitorsPricePeriod: number;
  competitorsPriceMethod: CompetitorsPriceMethods;
  newProductPriceStrategy: NewProductPriceStrategy;
  priceGap: number;
  priceGapCtmPc: number;
  manualPennies: number | null;
  competitorsPriceAdjustment: string;
  competitorsInfluencesCoef: number;
  competitorsMinimumProfit: number;
  competitorsMaximumProfit: number;
  competitorsControlParam: number;
  competitorsPriceStrategy: CompetitorsPriceStrategies;
  roundKviCompetitorPrice: boolean;
  calculateByRegression: boolean;
  recalculateCtm: boolean;
  priceChangeLimit: number;
  calculatePYR: boolean;
  calculateCompAllowedDeviation: boolean;
  useCompElasticity: boolean;
  forecastAccuracy?: boolean;
  countAtMaxPurchasePrice?: boolean;
  useAllProductBatchNumbers?: boolean;
  priceOpt?: StrategyOfPriceOpt;
  lift: number;
  crossCalculation: boolean;
  promoType: PromoType;
  availableStockDelta: number;
  calculateCommon: boolean;
  intervalOfCorrelationElasticity: IntervalOfUltimateElasticity;
  useDiscountDurationForForecastCalculation: boolean;
  excludeOutOfStock: boolean;
  backBasketPart?: number;
  percentileType: PercentileTypes;
  checkMarginDeclineProfit?: boolean;
  declineProfitabilityPc?: number;
  searchPreviousPricesDepth?: number;
  compensateLowerTargets?: boolean;
  spreadByPriceSegments: boolean;
}

// tslint:disable-next-line: interface-name
export interface KviCartFilters {
  kviCartIdFilter?: number;
  kviCartNameFilter?: string;
  internalCodesAndSkuNames?: InternalCodeAndSkuName[];
  kviCartId: number;
  separateClusters: boolean;
  coefficientK1: number; // +
  coefficientK2: number; // +
  category: number | null;
  storeCluster: number[] | number | null;
  storeRegionValues: number[] | null;
  groupsOfProduct: number[] | null;
  categories: number[] | null;
  subcategories: number[] | null;
  productSegments: number[] | null;
  storeFormat: number | null;
  groupOfProduct: number | null;
  subcategory: number | null;
  segment: number | null;
  tradingId: number;
  competitorsPricesParams: number; // +
  kviStrategy: KviStrategies; // +
  percentageOfCompetitorsPrice: string; // +
  marginalProfit: number; // +
  marginalProfitKvi?: number; // +
  marginalProfitCompetitors?: number; // +
  maxMarginalProfit: number; // +
  priceIncreaseLimit: number; // +
  fixLowerBorder: boolean; // +
  individualCommonParams: CalcIndividualConfigMap;
  priceOpt: StrategyOfPriceOpt; // +
  useCompetitorsPrices: boolean; // +
  byPromoPrices: boolean; // +
  competitorsPricePeriod: number; // +
  competitorsCodes: string[]; // +
  competitorsPriceMethod: CompetitorsPriceMethods; // +
  competitorsPriceAdjustment: string; // +
  competitorsInfluencesCoef: number; // +
  competitorsMinimumProfit: number; // +
  competitorsMaximumProfit: number; // +
  competitorsControlParam: number; // +
  competitorsPriceStrategy: CompetitorsPriceStrategies; // +
  daysToPriceChangeStart?: number | null;
  daysToPriceChangeFinish?: number | null;
  roundKviCompetitorPrice: boolean; // +
  priceChangeLimit: number;
  priceChangeLimitKvi: number;
  priceChangeLimitComp: number;
  periodPriceIncreaseLimit: number;
  period: number;
  percentageOfRecommendsKviCount?: number | null;
  saveKviSign?: boolean | null;
  useSegmentsPriceInSearchKVIRecParam?: boolean | null;
  useCompetitorsForKviRecommends?: boolean | null;
  competitorsPeriodForKviRecommends?: number | null;
  productCodesForAddKvi: string[];
  productCodesForDeleteKvi: string[];
  productCodesForAddCtmPurpose: string[];
  productCodesForDeleteCtmPurpose: string[];
  roundPricesWithEqualCompStrategy: boolean;
  calculateCompAllowedDeviation: boolean;
  useCompElasticity: boolean;
  minProfitKVI: number;
  maxProfitKVI: number;
  productRangesAndInternalCodes: InternalCodeAndProductRange[];
  kviCartChangePrices: KviCartChangePrices;
  percentileType: PercentileTypes;
  competitorPriceControlParameterPc: number;
  revaluationCalculateParams: RevaluationCalculateParams;
  checkMarginDeclineProfit?: boolean;
  declineProfitabilityPc?: number;
  searchPreviousPricesDepth?: number;
  thompsonSampling: boolean;
  downloadParamsWithCategory: boolean;
  isFixPricePromo: boolean;
  autoApprovalParams?: AutoApprovalParams;
}

// tslint:disable-next-line: interface-name
export interface PromoFilters extends CalcFilters {
  crossCalculation: boolean;
  correlation: number;
  promoInternalCodes: string[];
  promoType?: PromoType | null;
  lift: number;
  purchasePrice?: number | null;
  individualPromoConfigs: PromoIndividualConfigMap;
  availableStockDelta: number;
  calculateCommon: boolean;
  intervalOfCorrelationElasticity: IntervalOfUltimateElasticity;
  useDiscountDurationForForecastCalculation: boolean;
  useProductRange: boolean;
  useSkuRotation: boolean;
  rotationPeriodType: SkuRotationPeriodType;
  excludeOutOfStock: boolean;
  promoQuote: number | null;
  promoPriceCalculationDepth: number;
  useCompetitorsPromoPrices: boolean;
  calculateCompAllowedDeviationPromo: boolean;
  competitorPriceControlParameterPcPromo: number;
  competitorsPricePeriodPromo: number;
  competitorsPromoPriceInfluencesCoef: number;
  percentileTypePromo: PercentileTypes;
  competitorsPromoPriceAdjustment: string;
  competitorsControlPromoParam: number;
  competitorsPromoPriceMethod: CompetitorsPriceMethods;
  competitorsMinimumProfitPromo: number;
  competitorsMaximumProfitPromo: number;
  competitorsPromoPriceStrategy: CompetitorsPriceStrategies;
  planningPromoPeriodStartDate: number | null;
  planningPromoPeriodEndDate: number | null;
  minDefaultDiscount: number;
  maxDefaultDiscount: number;
}

export interface SalesFilters extends PromoFilters {
  considerPlannedSeason: boolean;
  calculateBySalePoints: boolean;
  calculateBySubcategories: boolean;
  trainModel: boolean;
  considerCannibalization: boolean;
  priceDecreaseLimit?: number;
  minPromoPrice?: number;
  autoCreateSchedule?: boolean;
}

export interface ModuleItemFilters extends PromoFilters {
  considerPlannedSeason: boolean;
  calculateBySalePoints: boolean;
  calculateBySubcategories?: boolean;
  trainModel: boolean;
  considerCannibalization: boolean;
  priceDecreaseLimit?: number;
  minPromoPrice?: number;
  calculationType?: string;
}

export interface SalesCalculationFilters extends SalesFilters {
  saleId: number | null;
  saleName: string | null;
  remains?: number | null;
  minProfitability: number | null;
  maxProfitability: number | null;
  minDiscount: number | null;
  maxDiscount: number | null;
  stagesNumber: number | null;
  startDate: number | null;
  endDate: number | null;
  stageNumber: number | null;
  minMargin: number | null;
}

export interface SaveModuleItemParamsFilter extends ModuleItemFilters {
  saleId?: number | null;
  saleName?: string | null;
  promotionId?: number | null;
  promotionName?: string | null;
  remains?: number | null;
  minProfitability: number | null;
  maxProfitability: number | null;
  minDiscount?: number | null;
  maxDiscount?: number | null;
  stagesNumber?: number | null;
  startDate: number | null;
  endDate: number | null;
  stageNumber?: number | null;
  minMargin: number | null;
}

export interface PromotionsCalculationFilters extends PromoFilters {
  promotionId: number | null;
  promotionName: string | null;
  remains?: number | null;
  minProfitability: number | null;
  maxProfitability: number | null;
  minDiscount: number | null;
  maxDiscount: number | null;
  stagesNumber: number | null;
  startDate: number | null;
  endDate: number | null;
  stageNumber?: number | null;
  minMargin: number | null;
}

export interface ModuleItemCalculationStartParams extends ModuleItemFilters {
  saleId?: number | null;
  saleName?: string | null;
  promotionId?: number | null;
  promotionName?: string | null;
  minProfitability: number | null;
  maxProfitability: number | null;
  minDiscount?: number | null;
  maxDiscount?: number | null;
  stagesNumber?: number | null;
  startDate: number | null;
  endDate: number | null;
  stageNumber?: number | null;
  minMargin: number | null;
}



// tslint:disable-next-line: interface-name
export interface OKRBCodesConfig {
  tradingId: number;
  categories: number[] | null | string;
  groupsOfProduct: number[] | null | string;
  subcategories: number[] | null | undefined | string;
}

// tslint:disable-next-line: interface-name
export interface OKRBCodeConfig {
  tradingId: number;
  internalCode: string[];
}

// tslint:disable-next-line: interface-name
export interface PackageCalcConfig {
  tradingId: number;
  categories: number[] | null;
  storeCluster: number[] | null;
  seasonValues?: number[] | null;
  buyerSegments?: number[] | null;
  storeRegionValues: number[] | null;
  storeFormat: number[] | null;
  calculationDepth?: number;
  calculationDay?: number;
  groupsOfProduct: number[] | null;
  subcategories: number[] | null;
  productSegments?: number[] | null;
  separateSubcategories: boolean;
  separateClusters: boolean;
  allCheck?: boolean;
  discountPrise?: boolean;
  pennies?: boolean;
  allNewPricesWithoutPennies?: boolean;
  allClaster?: boolean;
  coefficientK1?: number;
  coefficientK2?: number;
  depthToPurchasePrice?: number;
  priceOpt?: StrategyOfPriceOpt;
  calculateByRegression?: boolean;
  packageCalc?: boolean;
  categoryStrategy?: CategoryStrategies;
  sendReportToFtp?: boolean;
  makeGeneralReport?: boolean;
  manualPennies?: number | null;
  calcWithBatchNumber: boolean;
  backBasketPart?: number;
  compensateAllCategories?: boolean;
  thompsonSampling: boolean;
  revaluationCalculateParams?: RevaluationCalculateParams;
  compensateLowerTargets?: boolean;
  applyOmnichannel: boolean;
  sendGeneralReportNotification: boolean;
  isFixPricePromo: boolean;
  useSkuRotation: boolean;
  useFixedPrices: boolean;
  autoApprovalParams?: AutoApprovalParams;
  priceExperiment?: boolean;
  priceExperimentSampleSize?: number;
  priceExperimentDiscountRangeFrom?: number;
  priceExperimentDiscountRangeTo?: number;
  priceExperimentStep?: number;
  priceExperimentStartDate?: string | null;
  priceExperimentEndDate?: string | null;
  strategyValidityBeginDate?: number | null;
  strategyValidityEndDate?: number | null;
}

// tslint:disable-next-line: interface-name
export interface FilterTree {
  tradingId: number;
  categories: number[];
  storeCluster: number[] | null;
  categoryStrategy: CategoryStrategies;
  productSegments: number[] | null;
  seasonValues?: number[] | null;
  buyerSegments?: number[] | null;
  storeRegionValues: number[] | null;
  storeFormat: number[];
  groupsOfProduct: number[] | null;
  subcategories?: number[] | null;
  separateSubcategories: boolean;
  separateClusters: boolean;
}

// tslint:disable-next-line: interface-name
export interface SmallEventFilterTree {
  tradingId: number | null;
  categories: number | null;
  storeCluster: number | null;
  kviCartId: number | null;
  // seasonValues?: number[] | null;
  // buyerSegments?: number[] | null;
  storeRegions: number | null;
  storeFormat: number | null;
  groupsOfProduct: number | null;
  subcategories?: number | null;
  // separateSubcategories: boolean;
  // separateClusters: boolean;
}

// tslint:disable-next-line: interface-name
export interface RequestEventList extends SmallEventFilterTree {
  watchEventsType: WatchEventsType;
  limit: number;
  offset: number;
}

export interface ManuallyEventsWatchingParams {
  allRowsSelected: boolean;
  included: number[];
  excluded: number[];
}

// tslint:disable-next-line: interface-name
export interface EventFilter extends FilterTree {
  startDate: string;
  endDate: string;
  delta: number;
  startCalculation: boolean;
  onlyChangedPrices: boolean;
  deployPrices: boolean;
  createCommonReport: boolean;
  changingType: LoadEventChangingType;
  periodicity: LoadEventPeriods;
  priority: number;
  timeout: number;
  separateSubcategories: boolean;
  separateClusters: boolean;
  type: LoadEventType;
  applyOmnichannel: boolean;
  kviCartId: number | null;
  kviCartName: string | null;
  autoApprovalParams?: AutoApprovalParams;
}

// tslint:disable-next-line: interface-name
export interface SmallEventFilter {
  startDate: string;
  endDate: string;
  delta: number;
  startCalculation: boolean;
  onlyChangedPrices: boolean;
  deployPrices: boolean;
  createCommonReport: boolean;
  changingType: LoadEventChangingType;
  periodicity: LoadEventPeriods;
  applyOmnichannel: boolean;
  kviCartId: number;
  kviCartName: string;
  priority: number;
  timeout: number;
  separateSubcategories: boolean;
  separateClusters: boolean;
  type: LoadEventType;
  id: number;
  tradingId: number;
  categories: string;
  storeCluster: string | null;
  // seasonValues?: number[] | null;
  // buyerSegments?: number[] | null;
  storeRegions: string | null,
  storeFormat: string | null,
  groupsOfProduct: string | null | any[],
  categoriesOfProduct?: string | null | any[],
  subcategories?: string | null | any[],
  formatSelected?: string | null | any[],
  regionSelected?: string | null | any[],
  clusterSelected?: string | null | any[],
  autoApprovalParams?: AutoApprovalParams;
}

// tslint:disable-next-line: interface-name
export interface EventFile {
  tradingNetworkId: number;
  modified: string | null;
  type: FileType;
}

// tslint:disable-next-line: interface-name
export interface EventIndividualConfigs {
  startDate?: string | null;
  endDate?: string | null;
  delta?: number | null;
  startCalculation?: boolean | null;
  onlyChangedPrices?: boolean | null;
  deployPrices?: boolean | null;
  createCommonReport?: boolean | null;
  changingType?: LoadEventChangingType | null;
  type?: LoadEventType | null;
  periodicity?: LoadEventPeriods | null;
  priority?: number | null;
  timeout?: number | null;
  separateSubcategories?: boolean | null;
  separateClusters?: boolean | null;
  applyOmnichannel: boolean;
  kviCartId?: number | null;
  kviCartName?: string | null;
  autoApprovalParams?: AutoApprovalParams;
}

// tslint:disable-next-line: interface-name
export interface FoundEntityEvent {
  ids: number[],
}

export interface EventEditData {
  categories: ProductTag[];
  storeFormats: ProductTag[];
  storeRegions: ProductTag[];
  buyerSegments: ProductTag[];
  storeClusters: ProductTag[];
  groups: ProductTag[];
  psegments: ProductTag[];
  subcategories: ProductTag[];
  seasonSelected: ProductTag[];

  separateSubcategories: boolean;
  categoryStrategy: CategoryStrategies;
  separateClusters: boolean;
}

// tslint:disable-next-line: interface-name no-empty-interface
export interface PackagePromoConfig extends PackageCalcConfig {}

// tslint:disable-next-line: interface-name no-empty-interface
export interface PackageKviRecommendsConfig extends PackageCalcConfig {
  competitorsPricesParams: number;
  percentageOfCompetitorsPrice: string;
  kviStrategy: KviStrategies;
  minProfitKVI: number;
  maxProfitKVI: number;
  roundKviCompetitorPrice: boolean;
  percentageOfRecommendsKviCount?: number | null;
  useSegmentsPriceInSearchKVIRecParam?: boolean | null;
  saveKviSign?: boolean | null;
  useCompetitorsForKviRecommends?: boolean | null;
  competitorsPeriodForKviRecommends?: number | null;
  spreadUVWParams: UVWSpreadParams;
  spreadXYZParams: XYZSpreadParams;
  intervalOfUltimateElasticity: IntervalOfUltimateElasticity;
}

// tslint:disable-next-line: interface-name
export interface ScheduleCalcFilter extends PackageCalcConfig {
  startDate: string | null;
  endDate: string | null;
  schedulePeriod: SchedulePeriods;
  timeout: number;
  priority: number;
  kviCartId?: number | null;
  kviCartName?: string | null;
  saleId?: number;
  promoId?: number;
  priceExperimentGenerationMethod?: string | null;
}

// tslint:disable-next-line: interface-name
export interface KviCartPackageConfig extends PackageCalcConfig {
  kviCartChangePrices: KviCartChangePrices;
  kviCartId: number;
  kviCartsParams: KviCartFilters;
  percentileType: PercentileTypes;
}

// tslint:disable-next-line: interface-name
export interface ScheduleCalcColumns {
  key?: number;
  id: number;
  startDate: string;
  endDate: string;
  tradingName: string;
  categories: string | null;
  groupsOfProduct: string | null;
  subcategories: string | null;
  storeFormat: string | null;
  storeRegionValues: string | null;
  storeCluster: string | null;
  schedulePeriod: string;
  priority: number;
  timeout: number;
  kviCartId?: number | null;
  kviCartName?: string | null;
  sale?: string | null;
}

// tslint:disable-next-line: interface-name
export interface PreCalcColumns {
  id: number;
  tradingName: string;
  category: string | null;
  groupOfProduct: string | null;
  subcategories: string | null;
  storeFormat: string | null;
  storeRegionValues: string | null;
  storeCluster: string | null;
  calculationDepth: number;
  calculationDay: number;
  depthToPurchasePrice: number;
  calculateComparable: boolean;
  calculateActual: boolean;
  calculateAverage: boolean;
  calculatePurchasePrice: boolean;
  calculateRegularPrice: boolean;
  calculateCoeffElasticity: boolean;
  calculateSegment: boolean;
  status: string;
  statusMessage: string;
  updated: number;
}

// tslint:disable-next-line: interface-name
export interface Pageable {
  unpaged: boolean;
  paged: boolean;
  pageNumber: number;
  pageSize: number;
  offset: number;
  sort: Sort;
}

// tslint:disable-next-line: interface-name
// tslint:disable-next-line: interface-name
export interface SmallScheduleListFilter {
  tradingId: number;
  categories: number[];
  groupsOfProduct: number[];
  subcategories: number[];
  storeFormat: number | null;
  storeRegionValues: number[];
  storeCluster: number[];
  kviCartId: number | null;
  kviCartName: string | null;
}

export interface SmallSalesReportsScheduleListFilter {
  tradingId: number;
  saleIds?: number[];
  promoIds?: number[];
  startDate: string | null;
  endDate: string | null;
  categories: number[];
  groupsOfProduct: number[];
  subcategories: number[];
  storeFormat: number | null;
  storeRegionValues: number[];
  storeClusters: number[];
}

// tslint:disable-next-line: interface-name
export interface SmallPreCalcListFilter {
  tradingId: number | null;
  category: number | null;
  groupOfProduct: number | null;
  subcategories: number | null;
  storeFormat: number | null;
  storeRegionValues: number | null;
  storeCluster: number | null;
}

// tslint:disable-next-line: interface-name
export interface AllScheduleFiltersForTable {
  tradingName: ProductTag[];
  sales: ProductTag[];
  promoPrices: ProductTag[];
  categories: ProductTag[];
  groupsOfProduct: ProductTag[];
  subcategories: ProductTag[];
  storeFormat: ProductTag[];
  storeRegionValues: ProductTag[];
  storeCluster: ProductTag[];
}

// tslint:disable-next-line: interface-name
export interface AllPreCalcFilterNamesForTable {
  categories: ProductTag[];
  groupsOfProducts: ProductTag[];
  subcategories: ProductTag[];
  storeFormats: ProductTag[];
  storeRegionValues: ProductTag[];
  storeClusters: ProductTag[];
}

// tslint:disable-next-line: interface-name
export interface AllEventFiltersForTable {
  tradingName: ProductTag[];
  categories: ProductTag[];
  groupsOfProduct: ProductTag[];
  subcategories: ProductTag[];
  storeFormat: ProductTag[];
  storeRegionValues: ProductTag[];
  storeCluster: ProductTag[];
  kviCart: ProductTag[];
}

// tslint:disable-next-line: interface-name
export interface AllDetailReportFilterNames {
  taskIds: number[];
  messages: string[];
  groupNames: string[];
  categoryNames: string[];
  subCategoryNames: string[];
  formatNames: string[];
  regionNames: string[];
  clusterNames: string[];
  segmentNames: string[];
  internalCodes: string[];
  skuNames: string[];
  ctmBrands: string[];
  ctmPriceGaps: number[];
  minRetailPrices: number[];
  maxRetailPrices: number[];
  keyProducts1: string[];
  keyProducts2: string[];
  recipeAttributes: string[];
  treatmentAttributes: number[];
  producerPrices: number[];
  extraChargePcs: number[];
  registrationPrices: number[];
  skuLines: string[];
  purchasePrices: number[];
  promoPurchasePrices: number[];
  avgRegularPrices: number[];
  avgCashPrices: number[];
  avgCheckPrices: number[];
  regularPrices: number[];
  marginLastPrices: number[];
  minCompetitorPrices: number[];
  minCompetitorNames: string[];
  minCompetitorCodes: string[];
  maxCompetitorPrices: number[];
  maxCompetitorNames: string[];
  maxCompetitorCodes: string[];
  avgCompetitorPrices: number[];
  lastCompetitorPrices: number[];
  minPromoCompetitorPrices: number[];
  minPromoCompetitorNames: string[];
  minPromoCompetitorCodes: string[];
  maxPromoCompetitorPrices: number[];
  maxPromoCompetitorNames: string[];
  maxPromoCompetitorCodes: string[];
  medianPromoCompetitorPrices: number[];
  lastPromoCompetitorPrices: number[];
  piValues: number[];
  actualSales: number[];
  actualRevenues: number[];
  actualMargins: number[];
  actualMarginPercents: number[];
  comparableSales: number[];
  comparableRevenues: number[];
  comparableMargins: number[];
  comparableMarginPcs: number[];
  newPrices: number[];
  newPromoPrices: number[];
  correctedRegularPrices: number[];
  correctedPromoPrices: number[];
  newMarginPcs: number[];
  newPIs: number[];
  deltaDiffPrices: number[];
  priceDiffPcs: number[];
  marginDiffs: number[];
  changedPIs: number[];
  discountDepths: number[];
  discountDurations: number[];
  exitSegments: string[];
  coeffElasticitys: number[];
  promoCoeffElasticitys: number[];
  salesForecasts: number[];
  revenueForecasts: number[];
  marginForecasts: number[];
  marginForecastPcs: number[];
  changeSales: number[];
  changeRevenues: number[];
  changeMargins: number[];
  salesDiffPcs: number[];
  revenueDiffPcs: number[];
  marginDiffPcs: number[];
  marginDiffSeconds: number[];
  individualPromoTypes: string[];
  individualPromoLifts: number[];
  salesForecastByRegularPrices: number[];
  revenueForecastByRegularPrices: number[];
  marginForecastByRegularPrices: number[];
  marginForecastByRegularPricePcs: number[];
  changeSalesByRegularPrices: number[];
  changeRevenueByRegularPrices: number[];
  changeMarginByRegularPrices: number[];
  salesDiffPcByRegularPrices: number[];
  revenueDiffPcByRegularPrices: number[];
  marginDiffPcByRegularPrices: number[];
  marginDiffSecondByRegularPrices: number[];
  individualPriceOptStrategy: string[];
  individualPriceIncreaseLimits: number[];
  individualPeriodPriceIncreaseLimits: number[];
  individualPeriods: string[];
  individualMarginalProfits: number[];
  individualKviStrategys: string[];
  individualPercentCorrections: string[];
  individualNewProductPriceStrategys: string[];
  individualPriceGapPercents: number[];
  batchNumbers: string[];
  approvedByFioList: string[];
  productVerticalLines: string[];
  salePointCodes: string[];
}

// tslint:disable-next-line: interface-name
export interface ScheduleIndividualConfigs {
  startDate?: string | null;
  endDate?: string | null;
  schedulePeriod?: SchedulePeriods | null;
  priority?: number | null;
  timeout?: number | null;
}

// tslint:disable-next-line: interface-name
export interface PreCalcIndividualConfigs {
  calculationDepth: number;
  calculationDay: number;
  depthToPurchasePrice: number;
  calculateComparable: boolean;
  calculateActual: boolean;
  calculateAverage: boolean;
  calculatePurchasePrice: boolean;
  calculateRegularPrice: boolean;
  calculateCoeffElasticity: boolean;
  calculateSegment: boolean;
}

// tslint:disable-next-line: interface-name
export interface Sku {
  barCode: string;
  ctm: string;
  ctmBrandPurpose: string;
  hasRetailPrices: boolean;
  internalCode: string;
  keyCharacteristicOfTheProductIsOne: string;
  keyCharacteristicOfTheProductIsTwo: string;
  keyCharacteristicOfTheProductIsThree: string;
  keyCharacteristicOfTheProductIsFour: string;
  keyCharacteristicOfTheProductIsFive: string;
  productGroupId?: number;
  productGroup: string;
  categoryId?: number;
  categoryName: string;
  subcategoryId?: number;
  subcategory: string;
  productFormatName: string;
  productRegionNames: string;
  productClusterName: string;
  recipeAttribute: string;
  treatmentAttribute: number;
  kvi: number;
  lastPrice: number;
  maxRetailPrice: number;
  minRetailPrice: number;
  priceGapSTM: number;
  priceSegmentMin: number;
  priceSegmentMax: number;
  productRange: string;
  skuName: string;
  priceSegments: number[];
  actualProfitability: number;
  purchasePriceAvgM: number;
  isNewProduct: boolean;
  priceCategoryRecommendation: boolean;
  cartGeneratorRecommendation: boolean;
  productSegmentId: number;
  productSegmentName: string;
  lastPriceMarginPc: number;
  batchNumber: string;
  basePrice: number;
  vatPercentage: number;
  limitRetailPrice: number;
  marketingPaymentsPercentage: number;
  logisticCostsAmount: number;
  logisticCostsPercentage: number;
  markupPercentage: number;
  productVolume: number | null;
  measurementUnit: string | null;
  productVerticalLine: string | null;
  isVerticalLineBase: boolean | null;
  vlStrategy: string | null;
  vlStrategyStr: string | null;
  vlGapPc: number | null;
  vlGapType: VlGapType;
  roundVerticalLine: boolean | null;
  competitorNames: string[] | null;
  kviCartProductId?: number;
  cartName?: string | null;
}

// tslint:disable-next-line: interface-name
export interface CompetitorNameAndFilter {
  competitorName: string;
  filter: string;
}

// tslint:disable-next-line: interface-name
export interface ResultTableHeaders {
  taskId: number;
  type: TaskType;
  fileResult: string;
  fileForRevaluationResult: string;
  status: TaskStatus;
  editable: boolean;
  ftpSendingStatus: string;
  groupName: string;
  categoryName: string;
  subCategoryName: string;
  formatName: string;
  regionName: string;
  clusterName: string;
  skuCountInGroup: number;
  priceTagChangeExpense: number;
  priceChangedCount: number;
  avgPcPriceChange: number;
  priceIncreaseCount: number;
  avgPcIncreasePrice: number;
  priceDecreaseCount: number;
  avgPcPriceDecrease: number;
  marginLoss: number;
  marginLossPc: number;
  comparableSalesAmount: number;
  realSalesAmount: number;
  forecastAmount: number;
  sumSalesForecastByRegularPrice: number;
  changeForecastSalesByRegularPrice: number;
  changeForecastSalesPcByRegularPrice: number;
  sumRevenueForecastByRegularPrice: number;
  changeForecastRevenueByRegularPrice: number;
  changeForecastRevenuePcByRegularPrice: number;
  sumMarginForecastByRegularPrice: number;
  changeForecastMarginByRegularPrice: number;
  changeForecastMarginPcByRegularPrice: number;
  sumMarginForecastPcByRegularPrice: number | null;
  changeMarginByRegularPricePp: number | null;
  amountChange: number;
  amountChangePc: number;
  revenueComparable: number;
  revenueReal: number;
  forecastRevenue: number;
  revenueChange: number;
  revenueChangePc: number;
  marginComparable: number;
  marginReal: number;
  forecastMargin: number;
  marginChange: number;
  marginChangePc: number;
  marginComparablePc: number;
  marginRealPc: number | null;
  forecastMarginPc: number | null;
  marginChangePp: number | null;
  promoSkuCountInGroup: number;
  promoAvgPcDiscount: number;
  promoAvgDiscountDuration: number;
  promoRealSales: number;
  promoForecastRealSales: number;
  promoRealSalesChange: number;
  promoPcRealSalesChange: number;
  promoRealRevenue: number;
  promoForecastRealRevenue: number;
  promoRealRevenueChange: number;
  promoPcRealRevenueChange: number;
  promoRealMargin: number;
  promoForecastRealMargin: number;
  promoRealMarginChange: number;
  promoPcRealMarginChange: number;
  promoRealMarginPc: number | null;
  promoForecastRealMarginPc: number | null;
  promoMarginChangePp: number | null;
  promoPartRevenue: number;
  promoPartSalesAmount: number;
  promoPartGrossMargin: number;
  promoPartSKU: number;
  settlementDate: Date;
  forecastDate: Date;
}

export interface SalesMonitorResultTableHeaders extends ResultTableHeaders {
  saleName: string,
  saleStartDate: string,
  saleType: string,
  saleEndDate: string,
  saleRemains?: number | null,
}

export interface PromotionsMonitorResultTableHeaders extends ResultTableHeaders {
  promotionName: string,
  promotionStartDate: string,
  promotionEndDate: string,
  promotionType: string,
  promotionRemains?: number | null,
}

// tslint:disable-next-line: interface-name
export interface ResultTableDetails {
  resultTableHeaders: ResultTableHeaders[];
  totalRows: number;
}

export interface SalesMonitorResultTableDetails {
  resultTableHeaders: SalesMonitorResultTableHeaders[];
  totalRows: number;
}

// tslint:disable-next-line: interface-name
export interface ResultTableDetailReports {
  id: string;
  taskId: number;
  type: string;
  message: string;
  groupName: string;
  categoryName: string;
  subCategoryName: string;
  internalCode: string;
  skuName: string;
  kvi: string;
  ctm: string;
  ctmBrand: string;
  ctmPriceGap: number;
  minRetailPrice: number;
  maxRetailPrice: number;
  keyProduct1: string;
  keyProduct2: string;
  minSegment: number;
  maxSegment: number;
  skuLine: string;
  purchasePrice: number;
  byBaseDepth: string;
  elasticityChanged: string;
  discountDepthAndDurationAverage: string;
  newPriceIsFirstRegularPrice: string;
  resultType: string;
  isReturnPrice: string;
  linearRegression: string;
  promoPurchasePrice: number;
  avgRegularPrice: number;
  avgCashPrice: number;
  avgCheckPrice: number;
  regularPrice: number;
  salesLoading: string;
  marginLastPrice: number;
  minCompetitorPrice: number;
  minPromoCompetitorPrice: number;
  minCompetitorName: string;
  minCompetitorCode: string;
  minPromoCompetitorName: string;
  minPromoCompetitorCode: string;
  maxCompetitorPrice: number;
  maxPromoCompetitorPrice: number;
  maxCompetitorName: string;
  maxCompetitorCode: string;
  maxPromoCompetitorName: string;
  maxPromoCompetitorCode: string;
  avgCompetitorPrice: number;
  medianPromoCompetitorPrice: number;
  lastCompetitorPrice: number;
  lastPromoCompetitorPrice: number;
  pi: number;
  promoPi: number;
  actualSales: number;
  actualRevenue: number;
  actualMargin: number;
  actualMarginPercent: number;
  comparableSales: number;
  comparableRevenue: number;
  comparableMargin: number;
  comparableMarginPc: number;
  newPrice: number;
  correctedRegularPrice: number | null;
  correctedPromoPrice: number | null;
  newPromoPrice: number;
  optimalNewPromoPrice: number;
  newMarginPc: number;
  newPI: number;
  deltaDiffPrice: number;
  priceDiffPc: number;
  marginDiff: number;
  changedPI: number;
  discountDepth: number;
  optimalDiscountDepth: number;
  discountDuration: number;
  exitSegment: string;
  coeffElasticity: number;
  promoCoeffElasticity: number;
  remains: number | null;
  salesForecast: number;
  salesForecastByRegularPrice: number;
  revenueForecast: number;
  revenueForecastByRegularPrice: number;
  marginForecast: number;
  marginForecastByRegularPrice: number;
  marginForecastPc: number;
  marginForecastByRegularPricePc: number;
  changeSales: number;
  changeSalesByRegularPrice: number;
  changeRevenue: number;
  changeRevenueByRegularPrice: number;
  changeMargin: number;
  changeMarginByRegularPrice: number;
  salesDiffPc: number;
  salesDiffPcByRegularPrice: number;
  revenueDiffPc: number;
  revenueDiffPcByRegularPrice: number;
  marginDiffPc: number;
  marginDiffPcByRegularPrice: number;
  marginDiffSecond: number;
  marginDiffSecondByRegularPrice: number;
  priceTagChangeExpense: number;
  individualPromoType: string;
  individualPromoLift: number;
  individualPriceOptStrategy: string;
  individualPriceIncreaseLimit: number;
  individualPeriodPriceIncreaseLimit: number;
  individualPeriod: string;
  individualMarginalProfit: number;
  individualKviStrategy: string;
  individualPercentCorrection: string;
  individualNewProductPriceStrategy: string;
  individualPriceGapPercent: number;
  newProduct: string;
  productSegmentName: string;
  formatName: string;
  regionName: string;
  clusterName: string;
  rSquare: number;
  newPriceRegression: number;
  newPriceBeforeSpread: number;
  newPriceAfterSpread: number;
  newCorellationPrice: number;
  purchasePriceWasCalculatedFromProducts: string;
  activeProduct: boolean;
  batchNumber: string;
  vetProductWithoutExtraChargePc: boolean;
  vetProductWithoutProducerAndRegistrationPrice: boolean;
  vetProductProducerPriceIsMoreThanRegistrationPrice: boolean;
  newPriceHistory: string;
  omnichannelStrategy?: string | null;
  omnichannelFixedGapType?: string | null;
  omnichannelGap?: number | null;
  omnichannelApplyCompetitorPrices?: boolean | null;
  omnichannelMinProfitabilityLimit?: boolean | null;
  omnichannelBaseChannelPrice?: number | null;
  eventTrigger: boolean;
  elasticityPriceCountError: boolean;
  recipeAttribute: string;
  treatmentAttribute: number;
  vitalMedicine: string;
  producerPrice: number;
  extraChargePc: number;
  registrationPrice: number;
  basePrice: number;
  vatPercentage: number;
  limitRetailPrice: number;
  marketingPaymentsPercentage: number;
  logisticCostsAmount: number;
  logisticCostsPercentage: number;
  wasPromoInRotationPeriod: boolean;
  isVerticalLineBase: boolean | null;
  isApproved: boolean;
  approvedByFio: string | null;
  approvalStatus: ApprovalStatus;
  approvedByUserId?: number | null;
  approvalDate?: string | null;
  status?: string;
  salePointCode?: string;
  priceExperiment?: boolean;
  priceExperimentSampleSize?: number;
  priceExperimentDiscountRangeFrom?: number;
  priceExperimentDiscountRangeTo?: number;
}

// tslint:disable-next-line: interface-name
export interface Segment {
  name: string;
  minSegment: number | null;
  maxSegment: number | null;
}

// tslint:disable-next-line: interface-name
export interface PromoFilesSkuNameAndInternalCode {
  internalCode: string;
  skuName: string;
  purchasePrice: number;
}

// tslint:disable-next-line: interface-name
export interface ProductSegmentIdAndName {
  segmentId: number | null;
  segmentName: string;
}

// tslint:disable-next-line: interface-name
export interface TaskData {
  id: number;
  taskId: number;
  tradingNetworks: SelectMarket;
  fileResult: string;
  fileForRevaluationResult: string;
  status: string;
  revaluationStatus: string;
  message: string;
  progress: number;
  updated: number;
  userFio: string;
  category: string;
  type: string;
  typeMachine: TaskType;
  cluster: string;
  region: string;
  format: string;
  group: string;
  subcategories: string[];
  ftpSendingStatus: string;
  editable: boolean;
  taskNote: string;
  needRecalculatePackageTask: boolean;
  fileForRtnReport: string;
  rtnReportGenerationStatus: string;
  saleName?: string;
  promoName?: string;
}

// tslint:disable-next-line: interface-name
export interface TaskNoteDTO {
  taskNote: string;
}

// tslint:disable-next-line: interface-name
export interface ProductTag {
  id: number;
  name: string;
}

// tslint:disable-next-line: interface-name
export interface ProductInfo {
  categories: ProductTag[];
  storeFormat: ProductTag[];
  storeRegion: ProductTag[];
  buyerSegments: ProductTag[];
  storeCluster: ProductTag[];
  existCountBuyer: boolean;
  groups: ProductTag[];
  psegments: ProductTag[];
  subcategories: ProductTag[];
}

// tslint:disable-next-line: interface-name
export interface UpdatedByUserInfo {
  updatedByUsers: string[];
}

export enum PromoFilterType {
  all = "all",
  pi = "pi",
  cd = "cd",
  ig = "ig",
}

// tslint:disable-next-line: interface-name
export interface PromoSku {
  sku: string;
  internalCode: string;
  elasticity: number;
  trafficGenerator: number;
  cartDriver: number;
  incomeGenerator: number;
  penIndex: boolean;
}

// tslint:disable-next-line: interface-name
export interface ServerFile {
  id: number;
  tradingNetworks: SelectMarket;
  filePath: string;
  statusMessage: string;
  status: string;
  warnings: string;
  type: string;
  progress: string;
  archived: boolean;
  watched: boolean;
}

// tslint:disable-next-line: interface-name
export interface DownLoadPercent {
  fileId: number;
  percentDownload: string;
}

// tslint:disable-next-line: interface-name
export interface FtpDownloadProgress {
  taskId: number;
  ftpSendStatus: string;
}

// tslint:disable-next-line: interface-name
export interface SalesPage<T> {
  data: T;
  status: number;
}

export interface Page<T> {
  content: T;
  totalElements: number;
}

export enum TaskType {
  PROMO = 'PROMO',
  SALES = 'SALES',
  REGULAR = 'REGULAR',
  PACKAGE_REGULAR = 'PACKAGE_REGULAR',
  PACKAGE_PROMO = 'PACKAGE_PROMO',
  BRUT_FORCE = 'BRUT_FORCE',
  SEARCH_KVI = 'SEARCH_KVI',
  PACKAGE_SCHEDULE = 'PACKAGE_SCHEDULE',
  PACKAGE_GENERAL = 'PACKAGE_GENERAL',
  PACKAGE_GENERAL_PROMO = 'PACKAGE_GENERAL_PROMO',
  PACKAGE_PROMO_RECOMMENDS = 'PACKAGE_PROMO_RECOMMENDS',
  PACKAGE_PROMO_GENERAL_RECOMMENDS = 'PACKAGE_PROMO_GENERAL_RECOMMENDS',
  PACKAGE_KVI_CARTS = 'PACKAGE_KVI_CARTS',
  PACKAGE_GENERAL_KVI_CARTS = 'PACKAGE_GENERAL_KVI_CARTS',
  PACKAGE_KVI_RECOMMENDS = 'PACKAGE_KVI_RECOMMENDS',
  PACKAGE_GENERAL_KVI_RECOMMENDS = 'PACKAGE_GENERAL_KVI_RECOMMENDS',
  PACKAGE_SCENARIO = 'PACKAGE_SCENARIO',
  PACKAGE_GENERAL_SCENARIO = 'PACKAGE_GENERAL_SCENARIO',
  PACKAGE_EVENTS = 'PACKAGE_EVENTS',
  PACKAGE_EVENTS_GENERAL = 'PACKAGE_EVENTS_GENERAL',
  PACKAGE_FIXED_PRICE = 'PACKAGE_FIXED_PRICE',
  PACKAGE_GENERAL_FIXED_PRICE = 'PACKAGE_GENERAL_FIXED_PRICE',
  PACKAGE_SALES = 'PACKAGE_SALES',
  PACKAGE_GENERAL_SALES = 'PACKAGE_GENERAL_SALES',
  PACKAGE_AGGREGATE_SALES = 'PACKAGE_AGGREGATE_SALES',
  PACKAGE_GENERAL_AGGREGATE_SALES = 'PACKAGE_GENERAL_AGGREGATE_SALES',
  PROMOTION = 'PROMOTION', // TODO возможно название другое будет
  PACKAGE_PROMOTION = 'PACKAGE_PROMOTION',
  PACKAGE_GENERAL_PROMOTION = 'PACKAGE_GENERAL_PROMOTION',
  PACKAGE_GENERAL_AGGREGATE_PROMOTION = 'PACKAGE_GENERAL_AGGREGATE_PROMOTION',
  REGULAR_PRICE_EXP = 'REGULAR_PRICE_EXP',
  SCHEDULED_PRICE_EXP = 'SCHEDULED_PRICE_EXP',
}

export const taskTypesRequiresConfirmation = [
  selectBoxWithI18nLabel(TaskType.REGULAR, 'selectOptions.taskTypes.regular'),
  selectBoxWithI18nLabel(TaskType.PROMO, 'selectOptions.taskTypes.promo'),
  selectBoxWithI18nLabel(TaskType.PACKAGE_REGULAR, 'selectOptions.taskTypes.packageRegular'),
  selectBoxWithI18nLabel(TaskType.PACKAGE_PROMO, 'selectOptions.taskTypes.packagePromo'),
  selectBoxWithI18nLabel(TaskType.BRUT_FORCE, 'selectOptions.taskTypes.brutForce'),
  selectBoxWithI18nLabel(TaskType.PACKAGE_SCHEDULE, 'selectOptions.taskTypes.packageSchedule'),
  selectBoxWithI18nLabel(TaskType.PACKAGE_GENERAL, 'selectOptions.taskTypes.packageGeneral'),
  selectBoxWithI18nLabel(TaskType.PACKAGE_GENERAL_PROMO, 'selectOptions.taskTypes.packageGeneralPromo'),
  selectBoxWithI18nLabel(TaskType.PACKAGE_GENERAL_KVI_CARTS, 'selectOptions.taskTypes.packageGeneralKviCarts'),
  selectBoxWithI18nLabel(TaskType.PACKAGE_SCENARIO, 'selectOptions.taskTypes.packageScenario'),
  selectBoxWithI18nLabel(TaskType.PACKAGE_GENERAL_SCENARIO, 'selectOptions.taskTypes.packageGeneralScenario'),
  selectBoxWithI18nLabel(TaskType.PACKAGE_EVENTS, 'selectOptions.taskTypes.packageEvents'),
  selectBoxWithI18nLabel(TaskType.PACKAGE_EVENTS_GENERAL, 'selectOptions.taskTypes.packageEventsGeneral'),
  selectBoxWithI18nLabel(TaskType.PACKAGE_GENERAL_FIXED_PRICE, 'selectOptions.taskTypes.packageGeneralFixedPrice'),
  selectBoxWithI18nLabel(TaskType.PACKAGE_SALES, 'selectOptions.taskTypes.packageSales'),
  selectBoxWithI18nLabel(TaskType.PACKAGE_GENERAL_SALES, 'selectOptions.taskTypes.packageGeneralSales'),
  selectBoxWithI18nLabel(TaskType.PACKAGE_GENERAL_AGGREGATE_SALES, 'selectOptions.taskTypes.packageGeneralAggregateSales'),
  selectBoxWithI18nLabel(TaskType.PACKAGE_PROMOTION, 'selectOptions.taskTypes.packagePromotion'),
  selectBoxWithI18nLabel(TaskType.PACKAGE_GENERAL_PROMOTION, 'selectOptions.taskTypes.packageGeneralPromotion'),
  selectBoxWithI18nLabel(TaskType.PACKAGE_GENERAL_AGGREGATE_PROMOTION, 'selectOptions.taskTypes.packageGeneralAggregatePromotion'),
  selectBoxWithI18nLabel(TaskType.REGULAR_PRICE_EXP, 'selectOptions.taskTypes.regularPriceExp'),
  selectBoxWithI18nLabel(TaskType.SCHEDULED_PRICE_EXP, 'selectOptions.taskTypes.scheduledPriceExp'),
];

export const backgroundGeneratableTaskTypes = [
  TaskType.REGULAR,
  TaskType.PACKAGE_REGULAR,
  TaskType.PACKAGE_GENERAL,
  TaskType.PACKAGE_KVI_CARTS,
  TaskType.PACKAGE_GENERAL_KVI_CARTS,
  TaskType.PACKAGE_FIXED_PRICE,
  TaskType.PACKAGE_GENERAL_FIXED_PRICE,
  TaskType.PACKAGE_EVENTS,
  TaskType.PACKAGE_EVENTS_GENERAL,
  TaskType.PACKAGE_SCHEDULE,
  TaskType.REGULAR_PRICE_EXP,
  TaskType.SCHEDULED_PRICE_EXP,
];

export const RTNDocumentTaskTypes = [
  TaskType.REGULAR,
  TaskType.PACKAGE_GENERAL,
  TaskType.PACKAGE_GENERAL_KVI_CARTS,
  TaskType.PACKAGE_GENERAL_FIXED_PRICE,
  TaskType.PACKAGE_EVENTS_GENERAL,
];

export enum MoneyCodes {
  RUB = "RUB",
  USD = "USD",
  EUR = "EUR",
  KZT = "KZT",
  BYN = "BYN",
  UAH = "UAH",
  UZS = "UZS",
  CAD = "CAD",
  GBP = "GBP",
  BRL = "BRL",
  CNY = "CNY",
}

export enum UpdateFrequency {
  ONE = 1,
  FIVE = 5,
  TEN = 10,
  THIRTY = 30,
}

export enum SalesLoadingType {
  RECEIPTS_SEPARATELY = "RECEIPTS_SEPARATELY",
  AGGREGATE_BY_SKU = "AGGREGATE_BY_SKU",
}

export enum SchedulePeriods {
  DAY = "DAY",
  WEEK = "WEEK",
  MONTH = "MONTH",
  NO_REPEAT = "NO_REPEAT",
}

export enum PromoFilterProducts {
  PROMO = "PROMO",
  REGULAR = "REGULAR",
  ALL = "ALL",
}

export enum TaskStatus {
  CALCULATING = "Calculating",
  CALCULATING_COMPLETE = "CalculatingComplete",
  ERROR = "Error",
  ERROREXPORT = "ErrorExport",
  WRITETOFILE = "WriteToFile",
  WRITETOFILECOMPLETE = "WriteToFileComplete",
  EMPTYDATA = "EmptyData",
  EXECUTIONEXCEPTION = "ExecutionException",
  PROMONOTRECOMMENDED = "PromoNotRecommended",
  ARITHMETICEXCEPTION = "ArithmeticException",
  RESTARTERROR = "RestartError",
  INQUEUE = "InQueue",
  CANCELREPORT = "CancelReport",
  ONAPPROVAL = "OnApproval",
  REJECTED = "Rejected",
  APPROVED = "Approved",
  PARTIALLY_APPROVED = "PartiallyApproved",
  PARTIALLY_REJECTED = "PartiallyRejected",
  SENT_TO_FINAL_APPROVAL = "SentToFinalApproval"
}

export enum KviSearchMethod {
  ML = 'ML',
  CLUSTERIZATION = 'CLUSTERIZATION'
}

export enum RevaluationExportFileFormat {
  SP_CSV = 'SP_CSV',
  AUCHAN_TXT = 'AUCHAN_TXT',
  LICARD_PETRONIX = 'LICARD_PETRONIX',
}

export const backgroundGeneratableTaskStatuses = [
  TaskStatus.WRITETOFILECOMPLETE,
  TaskStatus.ONAPPROVAL,
  TaskStatus.SENT_TO_FINAL_APPROVAL,
  TaskStatus.APPROVED,
  TaskStatus.REJECTED,
  TaskStatus.PARTIALLY_APPROVED,
  TaskStatus.PARTIALLY_REJECTED,
].map((v) => v.valueOf());

export const generatableRTNReportTaskStatuses = [
  TaskStatus.CALCULATING,
  TaskStatus.ERROR,
].map((v) => v.valueOf());

export function isTaskInBackgroundGeneratableTypesAndStatuses(
  taskType: TaskType,
  taskStatus: string
) {
  return (
    backgroundGeneratableTaskTypes.includes(taskType) &&
    backgroundGeneratableTaskStatuses.includes(taskStatus)
  );
}

export function isRTNDocumentGeneratableTypesAndStatuses(
  taskType: TaskType,
  taskStatus: string
) {
  return (
    RTNDocumentTaskTypes.includes(taskType) &&
    backgroundGeneratableTaskStatuses.includes(taskStatus)
  );
}

export enum FtpSendProgressStatus {
  REPORTNOTSEND = "ReportNotSend",
  SENDTOFTP = "SendToFtp",
  SENDTOFTPCOMPLETE = "SendToFtpComplete",
  ERRORFTPSEND = "ErrorFtpSend",
}

export enum PercentileTypes {
  Q1 = "Q1",
  Q2 = "Q2",
  Q3 = "Q3",
  Q4 = "Q4",
  Q5 = "Q5",
}

export const percentileTypesSb: PercentileTypeSb = {
  Q1: { value: PercentileTypes.Q1, label: "0-25" },
  Q2: { value: PercentileTypes.Q2, label: "25-50" },
  Q3: { value: PercentileTypes.Q3, label: "50-75" },
  Q4: { value: PercentileTypes.Q4, label: "75-100" },
  Q5: { value: PercentileTypes.Q5, label: "0-100" },
};

// tslint:disable-next-line: interface-name
export interface TaskParams extends PageParams {
  status?: TaskStatus;
  statusList?: TaskStatus[];
  tradingId?: number;
  type?: TaskType;
  group?: number;
  category?: number;
  subcategories?: string;
  storeFormat?: number;
  storeRegions?: string;
  storeCluster?: number;
  userFio?: string;
  dateBefore?: string;
  dateAfter?: string;
  showOnlyRevaluationReports?: boolean;
  taskIdFilter?: string | null;
  taskIdsFilter?: number[];
  taskNoteFilter?: string | null;
  taskUpdateDateFilter?: number | null;
  types?: string[];
  saleTypes?: string[];
  saleStatuses?: string[];
}

// tslint:disable-next-line: interface-name
export interface FiltersWithCheckSum<T> {
  checkSum: string;
  filters: T;
}

export enum UserRole {
  ROLE_USER = "ROLE_USER",
  ROLE_ADMIN = "ROLE_ADMIN",
}

export enum FtpConfigType {
  CONFIG_REPORTS_KEY = "ftp_reports_config",
  CONFIG_ARCHIVE_KEY = "ftp_archive_config",
}

type UserRoleSb = { [x in UserRole]: SelectBox<UserRole> };

export const userRoleSb: UserRoleSb = {
  ROLE_USER: selectBoxWithI18nLabel(UserRole.ROLE_USER, 'selectOptions.userRoles.user'),
  ROLE_ADMIN: selectBoxWithI18nLabel(UserRole.ROLE_ADMIN, 'selectOptions.userRoles.admin'),
};

export enum AdditionalRole {
  FINAL_APPROVER = 'FINAL_APPROVER',
}

// tslint:disable-next-line: interface-name
export interface Config {
  disallowedModules: string[];
  role: UserRole;
  accessToSystemParams: boolean;
  userId: number;
  permissions: string[];
  additionalRoles: AdditionalRole[];
}

// tslint:disable-next-line: interface-name
export interface User {
  id: number;
  username: string;
  email: string;
  fio: string;
  role: UserRole;
  enabled: boolean;
  accessToSystemParams: boolean;
  permissions: string[];
  additionalRoles: AdditionalRole[];
  vice: UserShortInfo | null;
}

export interface UserShortInfo {
  id: number;
  username: string;
  email: string;
  fio: string;
}

// tslint:disable-next-line: interface-name
export interface UserForm {
  username: string;
  enabled: boolean;
  accessToSystemParams: boolean;
  password?: string;
  fio: string;
  email: string;
  role: UserRole;
}

// tslint:disable-next-line: interface-name
export interface UserPermissions {
  accessToSystemParams: boolean;
  permissions: string[];
}

// tslint:disable-next-line: interface-name
export interface ConstraintError {
  property: string;
  value: any;
  message: string;
}

// tslint:disable-next-line: interface-name
export interface ValidationResponse {
  errorCount: number;
  errors: ConstraintError[];
}

// tslint:disable-next-line: interface-name
export interface ChangePassword {
  password: string;
}

// tslint:disable-next-line: interface-name
export interface TradingNetworkTreeItem {
  id: number;
  name: string;
  groups: GroupTreeItem[] | [];
  checked: number | 0;
  isOpen: boolean | false;
  key: number | 0;
  isLoading: boolean | false;
}

// tslint:disable-next-line: interface-name
export interface GroupTreeItem {
  id: number;
  name: string;
  categories: CategoryTreeItem[] | [];
  checked: number | 0;
  isOpen: boolean | false;
  key: number | 0;
  isLoading: boolean | false;
}

// tslint:disable-next-line: interface-name
export interface CategoryTreeItem {
  id: number;
  name: string;
  isChecked: boolean | false;
}

// tslint:disable-next-line: interface-name
export interface SetUserTradingNetworkTreeItem {
  id: number;
  groups: SetUserGroupTreeItem[];
  checked: number;
}

// tslint:disable-next-line: interface-name
export interface SetUserGroupTreeItem {
  id: number;
  categoryIds: number[];
  checked: number;
}

// tslint:disable-next-line: interface-name
export interface UserTradingNetworkTreeItem {
  id: number;
  groups: UserGroupTreeItem[];
  isChecked: boolean;
}

// tslint:disable-next-line: interface-name
export interface UserGroupTreeItem {
  id: number;
  categoryIds: number[];
  isChecked: boolean;
}

export class IndividualConfigCmd<T> extends FindFiltersParams {
  individualConfigs: T | null = null;
}

// tslint:disable-next-line: interface-name
export interface PackageTasksCount {
  count: number;
}

// tslint:disable-next-line: interface-name
export interface FtpParameters {
  serverAddress: string;
  serverPort: number;
  username: string | null;
  password: string | null;
  dataDirectory: string | null;
  tradingCompaniesFileTransferSettings: TradingAndFolderPath[] | null;
  configType: string;
}

// tslint:disable-next-line: interface-name
export interface FtpSettings {
  baseParams: FtpParameters | null;
  archiveParams: FtpParameters | null;
}

// tslint:disable-next-line: interface-name
export interface PreCalcStartSettings {
  id: number;
  startTimePreCalcSettings: string;
  mondayPreCalcSettings: boolean;
  tuesdayPreCalcSettings: boolean;
  wednesdayPreCalcSettings: boolean;
  thursdayPreCalcSettings: boolean;
  fridayPreCalcSettings: boolean;
  saturdayPreCalcSettings: boolean;
  sundayPreCalcSettings: boolean;
}

// tslint:disable-next-line: interface-name
export interface TradingAndFolderPath {
  trId: number | null;
  serverAddress: string;
  serverPort: number;
  username: string | null;
  password: string | null;
  rollingUpChecks: string | null;
  dataDirectory: string | null;
}

// tslint:disable-next-line: interface-name
export interface TradingAndMoney {
  trId: number | null;
  moneyCode: string;
}

// tslint:disable-next-line: interface-name
export interface FtpUsersAndTradings {
  userName: string | null;
  tradingIds: number[];
  editable: boolean;
}

// tslint:disable-next-line: interface-name
export interface TaskAndFile {
  id: number;
  fileResult: string | null;
  type: TaskType;
  status: string | null;
  editable: boolean;
  filterNames: string;
  tradingId?: number;
  taskNote?: string;
}

// tslint:disable-next-line: interface-name
export interface ReportDetailParams {
  selectTaskIdFilter: number[];
  selectGroupFilter: string[];
  selectCategoryFilter: string[];
  selectSubcategoryFilter: string[];
  selectFormatFilter: string[];
  selectRegionFilter: string[];
  selectClusterFilter: string[];
  selectSegmentNameFilter: string[];
  selectInternalCodeFilter: string[];
  selectSkuNameFilter: string[];
  eventTriggerCondition: number | null;
  kviCondition: number | null;
  ctmCondition: number | null;
  selectCtmBrandFilter: string[];
  selectCtmPriceGapFilter: number[];
  selectMinRetailPriceFilter: number[];
  selectMaxRetailPriceFilter: number[];
  selectKeyProduct1Filter: string[];
  selectKeyProduct2Filter: string[];
  selectRecipeAttributeFilter: string[];
  selectTreatmentAttributeFilter: number[];
  selectVitalMedicineFilter: number | null;
  selectProducerPriceFilter: number[];
  selectExtraChargePcFilter: number[];
  selectRegistrationPriceFilter: number[];
  priceSegmentsForDetailReport: ReportDetailSegments[];
  selectSkuLineFilter: string[];
  selectPurchasePriceFilter: number[];
  selectPromoPurchasePriceFilter: number[];
  selectAvgRegularPriceFilter: number[];
  selectAvgCashPriceFilter: number[];
  selectAvgCheckPriceFilter: number[];
  selectRegularPriceFilter: number[];
  selectMarginLastPriceFilter: number[];
  selectMinCompetitorPriceFilter: number[];
  selectMinCompetitorNameFilter: string[];
  selectMinCompetitorCodeFilter: string[];
  selectMaxCompetitorPriceFilter: number[];
  selectMaxCompetitorNameFilter: string[];
  selectMaxCompetitorCodeFilter: string[];
  selectAvgCompetitorPriceFilter: number[];
  selectLastCompetitorPriceFilter: number[];
  selectMinPromoCompetitorPriceFilter: number[];
  selectMinPromoCompetitorNameFilter: string[];
  selectMinPromoCompetitorCodeFilter: string[];
  selectMaxPromoCompetitorPriceFilter: number[];
  selectMaxPromoCompetitorNameFilter: string[];
  selectMaxPromoCompetitorCodeFilter: string[];
  selectMedianPromoCompetitorPriceFilter: number[];
  selectLastPromoCompetitorPriceFilter: number[];
  selectPiFilter: number[];
  selectActualSalesFilter: number[];
  selectActualRevenueFilter: number[];
  selectActualMarginFilter: number[];
  selectActualMarginPercentFilter: number[];
  selectComparableSalesFilter: number[];
  selectComparableRevenueFilter: number[];
  selectComparableMarginFilter: number[];
  selectComparableMarginPcFilter: number[];
  selectNewPriceFilter: number[];
  selectNewPromoPriceFilter: number[];
  selectCorrectedRegularPriceFilter: number[];
  selectCorrectedPromoPriceFilter: number[];
  selectNewMarginPcFilter: number[];
  selectNewPIFilter: number[];
  selectDeltaDiffPriceFilter: number[];
  selectPriceDiffPcFilter: number[];
  selectMarginDiffFilter: number[];
  selectChangedPIFilter: number[];
  selectDiscountDepthFilter: number[];
  selectDiscountDurationFilter: number[];
  selectExitSegmentFilter: string[];
  selectCoeffElasticityFilter: number[];
  selectPromoCoeffElasticityFilter: number[];
  selectSalesForecastFilter: number[];
  selectRevenueForecastFilter: number[];
  selectMarginForecastFilter: number[];
  selectMarginForecastPcFilter: number[];
  selectChangeSalesFilter: number[];
  selectChangeRevenueFilter: number[];
  selectChangeMarginFilter: number[];
  selectSalesDiffPcFilter: number[];
  selectRevenueDiffPcFilter: number[];
  selectMarginDiffPcFilter: number[];
  selectMarginDiffSecondFilter: number[];
  selectIndividualPromoTypeFilter: string[];
  selectIndividualPromoLiftFilter: number[];
  selectSalesForecastByRegularPriceFilter: number[];
  selectRevenueForecastByRegularPriceFilter: number[];
  selectMarginForecastByRegularPriceFilter: number[];
  selectMarginForecastByRegularPricePcFilter: number[];
  selectChangeSalesByRegularPriceFilter: number[];
  selectChangeRevenueByRegularPriceFilter: number[];
  selectChangeMarginByRegularPriceFilter: number[];
  selectSalesDiffPcByRegularPriceFilter: number[];
  selectRevenueDiffPcByRegularPriceFilter: number[];
  selectMarginDiffPcByRegularPriceFilter: number[];
  selectMarginDiffSecondByRegularPriceFilter: number[];
  selectIndividualPriceOptStrategyFilter: string[];
  selectIndividualPriceIncreaseLimitFilter: number[];
  selectIndividualPeriodPriceIncreaseLimitFilter: number[];
  selectIndividualPeriodFilter: string[];
  selectIndividualMarginalProfitFilter: number[];
  selectIndividualKviStrategyFilter: string[];
  selectIndividualPercentCorrectionFilter: string[];
  selectIndividualNewProductPriceStrategyFilter: string[];
  selectIndividualPriceGapPercentFilter: number[];
  selectApprovedByFioFilter: string[];
  selectIsApprovedFilter: string[];
  selectPriceExperimentFilter: string[];
  selectProductVerticalLineFilter: string[];
  selectBatchNumberFilter: string[];
  selectSalePointsFilter: string[];
  sortingField: SortingField | null;
  detailTableSkuFilter: string | null;
  taskType: TaskType | null;
  selectActiveProductsOnly: boolean;
}

// tslint:disable-next-line: interface-name
export interface RevaluationQueueFilters {
  taskIds: number[]
  taskTypes: string[]
  userNames: string[]
  senderUserNames: string[]
  groupNames: string[]
  categoryNames: string[]
  subcategoryNames: string[]
  formatNames: string[]
  regionNames: string[]
  clusterNames: string[]
  internalCodes: string[]
  batchNumbers: string[]
  skuNames: string[]
  regularPrices: number[]
  marginLastPrices: number[]
  newPrices: number[]
  promoPrices: number[]
  newMarginPcs: number[]
  priceDiffPcs: number[]
  sentDates: string[]
  customSendingDates: string[]
}

// tslint:disable-next-line: interface-name
export interface CodeSalesPointsFilters {
  codeSalesPoints: string[];
  formatNames: string[];
  regionNames: string[];
  clusterNames: string[];
}

// tslint:disable-next-line: interface-name
export interface RevaluationQueueItem {
  taskId: number;
  userName: string;
  senderUserName: string;
  cartName?: string | null;
  taskType?: string | null;
  message: string;
  groupName: string;
  categoryName: string;
  subcategoryName: string;
  formatName: string;
  regionName: string;
  clusterName: string;
  internalCode: string;
  batchNumber: string;
  skuName: string;
  regularPrice: number;
  salesLoading: string;
  marginLastPrice: number;
  newPrice: number;
  newMarginPc: number;
  priceDiffPc: number;
  newPriceHistory: string;
  sentDate: string;
  isIncluded: boolean;
  selectedForRollback: boolean;
}

// tslint:disable-next-line: interface-name
export interface RevertRevaluationItem {
  taskId: number;
  groupName: string;
  categoryName: string;
  subcategoryName: string;
  formatName: string;
  regionName: string;
  clusterName: string;
  internalCode: string;
}

// tslint:disable-next-line: interface-name
export interface RevaluationQueueListRequestParams {
  page: number;
  size: number;
  sort: SortingField;
  tradingId: number;
  isSent: boolean;
  showExcludedProducts: boolean;
}

// tslint:disable-next-line: interface-name
export interface AssortmentDateRequest {
  assortmentDate: string;
  tradingId: number;
  itemsForRollback: RevertRevaluationItem[];
  selectAll: boolean;
  excludedItems: RevertRevaluationItem[];
  filters: RevaluationQueueFilters;
}

export interface KviCartConflictingItemDto {
  internalCode: string;
  skuName: string;
  kviCartName: string;
  storeFormatName: string;
  storeRegionName: string;
  storeClusterName: string | null;
}

// tslint:disable-next-line: interface-name
export interface TaskIdToInternalCode {
  taskId: number;
  internalCode: string;
}

// tslint:disable-next-line: interface-name
export interface SortingField {
  fieldName: string;
  sortValue: number;
}

// tslint:disable-next-line: interface-name
export interface ReportDetailSegments {
  name: string;
  minSegment: number | null;
  maxSegment: number | null;
}

// tslint:disable-next-line: interface-name
export interface IdAndName {
  id: number;
  name: string;
}

// tslint:disable-next-line: interface-name
export interface InternalCodeAndProductRange {
  internalCode: string;
  productRange: string;
}

// tslint:disable-next-line: interface-name
export interface InternalCodeAndSkuName {
  internalCode: string;
  skuName: string;
  storeFormat: string | null;
  storeRegion: string | null;
  storeCluster: string | null;
  lastPrice?: number | null;
  kviCartProductId?: number;
}

export interface ISaleSelectedProduct {
  internalCode: string;
  skuName: string;
  group: string;
  category: string | null;
  subcategory: string | null;
  storeRegion: string | null;
  storeCluster: string | null;
  lastPrice?: number | null;
}

export interface IModuleItemSelectedProduct {
  internalCode: string;
  skuName: string;
  group: string;
  category: string | null;
  subcategory: string | null;
  storeRegion: string | null;
  storeCluster: string | null;
  lastPrice?: number | null;
}

// tslint:disable-next-line: interface-name
export interface ParamsListItem {
  commonParams: CalcFilters;
  diff: string[];
  id: number;
  tradingNetwork: IdAndName;
  group: IdAndName | null;
  category: IdAndName | null;
  subcategories: IdAndName[] | null;
  storeFormat: IdAndName | null;
  storeRegions: IdAndName[] | null;
  storeCluster: IdAndName | null;
  categoryStrategy: CategoryStrategies;
  individualId: number | null;
  cartName?: string | null;
}

// tslint:disable-next-line: interface-name
export interface EventsListItem {
  id: number;
  created: number | null;
  eventId: number | null;
  fileId: number | null;
  event: SmallEventFilter;
  file: EventFile | null;
}

// tslint:disable-next-line: interface-name
export interface EventsListProductItem {
  id: number;
  group: IdAndName | null;
  category: IdAndName | null;
  subcategory: IdAndName | null;
  storeFormat: IdAndName | null;
  storeRegion: IdAndName | null;
  storeCluster: IdAndName | null;
  internalCode: string | null;
  delta: number | null;
}

// tslint:disable-next-line: interface-name
export interface EventsParameters {
  timeout: number;
  priority: number;
}

// tslint:disable-next-line: max-classes-per-file
export class EventsCalcParams {
  tradingId?: number | null;
  categories?: number[] | null;
  groupsOfProduct?: number[] | null;
  subcategories?: number[] | null;
  productSegments?: number[] | null;
  storeFormat?: number[];
  storeRegionValues?: number[] | null;
  storeCluster?: number[] | null;
  categoryStrategy?: string | null;
  separateSubcategories?: boolean | null;
  separateClusters?: boolean | null;
  storeSegment?: number[] | null;
  autoApprovalParams?: AutoApprovalParams;
}

// tslint:disable-next-line: max-classes-per-file
export class EventsAddCalcParams extends EventsCalcParams {
  timeout?: number | null;
  priority?: number | null;
}

// tslint:disable-next-line: interface-name
export interface RevaluationQuotasOfTradingNetwork {
  tradingNetworkId: number | null;
  quotasOfFormats: RevaluationQuotasOfStoreFormat[];
  availableFormats: IdAndName[];
}

// tslint:disable-next-line: interface-name
export interface RevaluationQuotasOfStoreFormat {
  storeFormatId: number | null;
  priorityRule: PriorityRule;
  dailyQuotas: DailyQuotas;
}

// tslint:disable-next-line: interface-name
export interface RevaluationQuotasToSave {
  tradingNetworkId: number;
  storeFormatId: number;
  priorityRule: PriorityRule;
  dailyQuotas: DailyQuotas;
}

// tslint:disable-next-line: interface-name
export interface PriorityRule {
  priority: ProductPriority;
  competitorsAndKviFirst: boolean;
  regulatedProductsFirst: boolean;
  ignoreEmptyRemainsInQuotas: boolean;
}

// tslint:disable-next-line: max-classes-per-file
export class CalcParams extends EventsCalcParams {
  timeout?: number | null;
  priority?: number | null;
  limit?: number | null;
  offset?: number | null;
  dateBefore?: string | null;
  dateAfter?: string | null;
  status?: string | null;
}

export interface TaskHeadersAndKeyIndicatorsParams {
  tradingId: number | null;
  limit: number | null;
  offset: number | null;
  dateBefore: string | null;
  dateAfter: string | null;
  groupsOfProduct: number[] | null;
  categories: number[] | null;
  subcategories: number[] | null;
  storeFormat: number[];
  storeRegionValues: number[] | null;
  storeCluster: number[] | null;
  status: string | null;
  types?: string[];
  categoryStrategy?: string | null;
  separateSubcategories?: boolean | null;
  separateClusters?: boolean | null;
  storeSegment?: number[] | null;
  timeout?: number | null;
  priority?: number | null;
}

export type QueryParams = {
  limit?: number | null;
  offset?: number | null;
  tradingId?: number | null;
  categories?: number[] | null;
  groupsOfProduct?: number | null;
  subcategories?: number[] | null;
  storeFormat?: number[];
  storeRegionValues?: number[] | null;
  storeCluster?: number[] | null;
  categoryStrategy?: string | null;
  separateSubcategories?: boolean | null;
  separateClusters?: boolean | null;
  storeSegment?: number[] | null;
  timeout?: number | null;
  priority?: number | null;
};

// tslint:disable-next-line: interface-name
export interface RevaluationQuotesData {
  id: number | null;
  tradingName: string | null;
  tradingId: number | null;
  monday: number;
  tuesday: number;
  wednesday: number;
  thursday: number;
  friday: number;
  saturday: number;
  sunday: number;
}

// tslint:disable-next-line: interface-name
export interface DailyQuotas {
  monday: number;
  tuesday: number;
  wednesday: number;
  thursday: number;
  friday: number;
  saturday: number;
  sunday: number;
}

// tslint:disable-next-line: interface-name
export interface RevaluationFtpParams {
  sendRevaluationAutomatically: boolean;
  ftpSendingTime: string | null;
}

export interface OperatingExpensesParams {
  tradingId: number;
  operatingExpenses: number;
}

export interface IGetReportDetailFieldsByUserIdParams {
  taskId?: number;
}

// tslint:disable-next-line: interface-name
export interface ReportDetailFieldsView {
  key: string;
  label: string;
  permanentlyHidden?: boolean;
}

// tslint:disable-next-line: interface-name
export interface ReportDetailColumnsForAddOrDelete {
  detailColumnsForAdd: ReportDetailFieldsView[];
  detailColumnsForDelete: ReportDetailFieldsView[];
}

// tslint:disable-next-line: interface-name
export interface SkuCharacteristicsKeysAndAttributes {
  keyProducts1: string[];
  keyProducts2: string[];
  keyProducts3: string[];
  keyProducts4: string[];
  keyProducts5: string[];
  recipeAttributes: string[];
  treatmentAttributes: number[];
}

// tslint:disable-next-line: interface-name
// tslint:disable-next-line: max-classes-per-file
export class DetailReportsPricesAndCodes {
  changesPrices: ChangesPrices[] = [];
  isAllSkusSelected: boolean = false;
  selectActiveProductsOnly: boolean = false;
  filterParams!: ReportDetailParams;
}

// tslint:disable-next-line: interface-name
// tslint:disable-next-line: max-classes-per-file
export class ChangesPrices {
  taskId: number = -1;
  codeWithPrices: InternalCodesWithCorrPrices[] = [];
  productCodesForDelete: string[] = []; // to remove
  productCodesForAdd: string[] = []; // to remove
  includedSkus: string[] = [];
  excludedSkus: string[] = [];
}

// tslint:disable-next-line: interface-name
// tslint:disable-next-line: max-classes-per-file
export class InternalCodesWithCorrPrices {
  internalCode: string = "";
  correctedPrice: number | null = null;
}

// tslint:disable-next-line: max-classes-per-file
export class ParametersListQuery {
  limit: number = 20;
  offset: number = 0;
  tnId?: number;
  gids?: number[];
  cids?: number[];
  subcids?: number[];
  sfids?: number[];
  srids?: number[];
  scids?: number[];
  updatedBy?: string;
}

export interface ParametersListFilterData {
  gids?: number[]; // groups
  cids?: number[]; // categories
  subcids?: number[]; // subcategories
  sfids?: number[]; // store formats
  srids?: number[]; // store regions
  scids?: number[]; // store clusters
  updatedBy?: string; // user
}

export interface AppliedFiltersData {
  selectedGroups?: number[] | null;
  selectedCategories?: number[] | null;
  selectedSubcategories?: number[] | null;
  selectedStoreFormats?: number[] | null;
  selectedStoreRegions?: number[] | null;
  selectedStoreClusters?: number[] | null;
}

// tslint:disable-next-line: max-classes-per-file
export class EventsListQuery {
  ascTn: boolean = true;
  ascG: boolean = true;
  ascC: boolean = true;
  ascSubc: boolean = true;
  ascSF: boolean = true;
  ascSR: boolean = true;
  ascSC: boolean = true;
  ascInternalCode: boolean = true;
  limit: number = 20;
  offset: number = 0;

  categories?: number | null;
  groupsOfProduct?: number | null;
  subcategories?: number | null;
  storeFormat?: number | null;
  storeRegionValues?: number | null;
  storeCluster?: number | null;
}

export interface IGrabbingConfigDTO {
  tradingNetId: number;
  tradingNetName: string;
  url: string;
  assortmentSchedule: IGrabSchedule;
  salesSchedule: IGrabSchedule;
  competitorSchedule: IGrabSchedule;
  emailAddresses: string;
  packageSize: number;
}

export interface ISaveGrabbingConfigDTO {
  tradingNetId: number;
  url: string;
  assortmentSchedule: IGrabSchedule;
  salesSchedule: IGrabSchedule;
  competitorSchedule: IGrabSchedule;
  emailAddresses: string;
  packageSize: number;
  accessToken: string | null;
}

export interface IGrabSchedule {
  time: string;
  monday: boolean;
  tuesday: boolean;
  wednesday: boolean;
  thursday: boolean;
  friday: boolean;
  saturday: boolean;
  sunday: boolean;
}

export interface ICallGrabbingDTO {
  tradingNetId: number;
  tradingNetName: string;
  grabItems: GrabItem[];
  salesDateFrom: string | null;
  salesDateTo: string | null;
}

export enum GrabItem {
  ASSORTMENT = "ASSORTMENT",
  SALES = "SALES",
  COMPETITORS = "COMPETITORS",
}

type GrabItemLabels = { [x in GrabItem]: string };

const grabItemI18nKeys: Record<GrabItem, string> = {
  [GrabItem.ASSORTMENT]: 'settings.grabber.grabItems.assortment',
  [GrabItem.SALES]: 'settings.grabber.grabItems.sales',
  [GrabItem.COMPETITORS]: 'settings.grabber.grabItems.competitors',
};

export const grabItemLabels: GrabItemLabels = new Proxy({} as GrabItemLabels, {
  get(_target, prop: string) {
    const key = grabItemI18nKeys[prop as GrabItem];
    
    return key ? i18n.t(key) as string : prop;
  },
});

export interface IErrors {
  errorCount: number;
  errors: IError[];
}

export interface IError {
  isGlobal: boolean;
  property: string;
  value: any;
  message: string;
}

export enum OmnichannelStrategy {
  PRICE_EQUALITY = "PRICE_EQUALITY",
  FIXED_DIFFERENCE = "FIXED_DIFFERENCE",
  OPTIMAL_DIFFERENCE_ML = "OPTIMAL_DIFFERENCE_ML",
}

export enum OmnichannelFixedGapType {
  GREATER_THAN = "GREATER_THAN",
  LESS_THAN = "LESS_THAN",
  NOT_GREATER_THAN = "NOT_GREATER_THAN",
  NOT_LESS_THAN = "NOT_LESS_THAN",
  FIX_GAP = "FIX_GAP",
}

// sale
export enum SaleFixedGapType {
  GREATER_THAN = "GREATER_THAN",
  LESS_THAN = "LESS_THAN",
  NOT_GREATER_THAN = "NOT_GREATER_THAN",
  NOT_LESS_THAN = "NOT_LESS_THAN",
}
// sale
export enum SaleIntersectionType {
  DUPLICATE = "DUPLICATE",
  CONTRADICT = "CONTRADICT",
}

export enum OmnichannelIntersectionType {
  DUPLICATE = "DUPLICATE",
  CONTRADICT = "CONTRADICT",
}

export class Sort {
  field!: string;
  asc!: boolean;
}

export enum ImportUploadOption {
  FULL_UPDATE = "FULL_UPDATE",
  UPDATE = "UPDATE",
  ADD_TO_EXISTING = "ADD_TO_EXISTING",
}

export enum LastAssortmentUpdatingStatus {
  DONE = "DONE",
  FAILED = "FAILED",
  UPDATING_ASSORTMENT_BY_SALE_POINTS_DIRECTORY = "UPDATING_ASSORTMENT_BY_SALE_POINTS_DIRECTORY",
  UPDATING_ASSORTMENT_BY_ASSORTMENT_DIRECTORY = "UPDATING_ASSORTMENT_BY_ASSORTMENT_DIRECTORY",
}

export enum DirectoriesSynchronizationStatus {
  DONE = "DONE",
  FAILED = "FAILED",
  UPDATING_ASSORTMENT_BY_PRODUCT_LINE_DIRECTORY = "UPDATING_ASSORTMENT_BY_PRODUCT_LINE_DIRECTORY",
  UPDATING_ASSORTMENT_BY_ASSORTMENT_DIRECTORY = "UPDATING_ASSORTMENT_BY_ASSORTMENT_DIRECTORY",
  UPDATING_ASSORTMENT_BY_SALE_POINTS_DIRECTORY = "UPDATING_ASSORTMENT_BY_SALE_POINTS_DIRECTORY",
}

export function isDirectoriesSyncInProgress(
  status: DirectoriesSynchronizationStatus
): boolean {
  return status === DirectoriesSynchronizationStatus.UPDATING_ASSORTMENT_BY_PRODUCT_LINE_DIRECTORY
      || status === DirectoriesSynchronizationStatus.UPDATING_ASSORTMENT_BY_ASSORTMENT_DIRECTORY
      || status === DirectoriesSynchronizationStatus.UPDATING_ASSORTMENT_BY_SALE_POINTS_DIRECTORY;
}
export function getMessageByStatus(
  status: LastAssortmentUpdatingStatus
): string {
  switch (status) {
    case LastAssortmentUpdatingStatus.UPDATING_ASSORTMENT_BY_ASSORTMENT_DIRECTORY:
      return i18n.t('serverData.lastAssortmentUpdating.updatingByAssortmentDirectory') as string;
    case LastAssortmentUpdatingStatus.UPDATING_ASSORTMENT_BY_SALE_POINTS_DIRECTORY:
      return i18n.t('serverData.lastAssortmentUpdating.updatingBySalePointsDirectory') as string;
    default:
      return '';
  }
}

export const lastAssortmentUpdatingStatuses: LastAssortmentUpdatingStatus[] = [
  LastAssortmentUpdatingStatus.UPDATING_ASSORTMENT_BY_SALE_POINTS_DIRECTORY,
  LastAssortmentUpdatingStatus.UPDATING_ASSORTMENT_BY_ASSORTMENT_DIRECTORY,
];
export function isUpdatingStatus(
  status: LastAssortmentUpdatingStatus
): boolean {
  return lastAssortmentUpdatingStatuses.includes(status);
}

export enum EventsProcessingStatus {
  DONE = "DONE",
  FAILED = "FAILED",
  IN_PROGRESS = "IN_PROGRESS",
}
export function getEventsProcessingStatusMessage(
  status: EventsProcessingStatus
): string {
  switch (status) {
    case EventsProcessingStatus.IN_PROGRESS:
      return i18n.t('serverData.eventsProcessing.inProgress') as string;
    default:
      return '';
  }
}
export const eventsProcessingInProgressStatuses: EventsProcessingStatus[] = [
  EventsProcessingStatus.IN_PROGRESS,
];
export function isInProgressStatus(status: EventsProcessingStatus): boolean {
  return eventsProcessingInProgressStatuses.includes(status);
}

export interface SalePointsDirectoryItem {
  id: number;
  tradingId: number;
  storeFormat: string;
  storeRegion: string;
  storeCluster: string;
  codeSalesPoint: string;
  salePointName: string | null;
  postalCode: string | null;
  address: string | null;
  salePointStatusLabel: string | null;
  openDate: Date | null;
  closeDate: Date | null;
  storeLegalEntity: string | null;
  email: string | null;
}

export interface SalePointsDirectoryParams {
  tradingId: number;
  storeFormatId: number | null;
  storeRegionId: number | null;
  storeClusterId: number | null;
  codeSalesPoint: string | null;
  salePointName: string | null;
  salePointStatusEnumValue: string | null;
  page: number;
  size: number;
  sortField: string;
  sortDirection: string;
}

export interface SalePointsDirectoryFilterOptions {
  groups: ProductTag[];
  categories: ProductTag[];
  subcategories: ProductTag[];
  storeFormats: ProductTag[];
  storeRegions: ProductTag[];
  storeClusters: ProductTag[];
  salePointStatuses: string[];
}

export interface BaseFilterOptions {
  groups: IdAndName[];
  categories: IdAndName[];
  subcategories: IdAndName[];
  storeFormats: IdAndName[];
  storeRegions: IdAndName[];
  storeClusters: IdAndName[];
}

export interface IHttpGetFixedPricesFilterOptions {
  storeFormats: IdAndName[];
  storeRegions: IdAndName[];
  storeClusters: IdAndName[];
  groups: any[];
  categories: any[];
  subcategories: any[];
  regularPrices: number[];
}

export interface FixedPriceParams {
  tradingNetId: number;
  groupIds: number[];
  categoryIds: number[];
  subcategoryIds: number[];
  storeFormatIds: number[];
  storeRegionIds: number[];
  storeClusterIds: number[];
  codeSalesPoint: string | null;
  page: number;
  size: number;
  skuName: string | null;
  internalCode: string | null;
  dateBefore?: string | null;
  dateAfter?: string | null;
  regularPriceStartDate?: string | null;
  regularPriceEndDate?: string | null;
  fileUploadDate?: string | null;
  regularPrices?: number[];
  userName?: string | null;
  isActive?: boolean | null;
  uploadFileIdFilterValue?: number | null;
  selectedIds: number[];
}

export interface FixedPriceItem {
  id: number;
  tradingNetId: number;
  storeFormat: string;
  storeRegion: string;
  storeCluster: string;
  skuName: string;
  internalCode: string;
  codeSalesPoint: string;
  page: number;
  size: number;
  isActive?: boolean;
  username?: string;
  regularPriceEndDate?: number;
  regularPriceStartDate?: number;
  uploadFile?: string;
  groupName: string;
  categoryName: string;
  subcategoryName: string;
}

export interface IStoreLegalEntityTableParams {
  tradingId: number;
  page: number;
  size: number;
  storeRegionIds: string;
  legalEntitiesIds: string;
}

export interface IStoreLegalEntityTableItem {
  storeLegalEntityId: number;
  storeLegalEntityName: string;
  storeRegionId: number;
  storeRegionName: string;
}

export interface AssortmentDirectoryItem {
  id: number;
  tradingId: number;
  storeFormat: string | null;
  storeRegion: string | null;
  storeCluster: string | null;
  group: string | null;
  category: string | null;
  subcategory: string | null;
  internalCode: string;
  skuName: string;
  kvi: number | null;
  ctm: string | null;
  ctmBrandPurpose: string | null;
  priceGapStm: number | null;
  productLine: string | null;
  purchasePrice: number | null;
  minRetailPrice: number | null;
  maxRetailPrice: number | null;
  keyCharacteristicOfTheProductIsOne: string | null;
  keyCharacteristicOfTheProductIsTwo: string | null;
  keyCharacteristicOfTheProductIsThree: string | null;
  keyCharacteristicOfTheProductIsFour: string | null;
  keyCharacteristicOfTheProductIsFive: string | null;
  markupPercentage: number | null;
  basePrice: number | null;
  vatPercentage: number | null;
  logisticCostsAmount: number | null;
  logisticCostsPercentage: number | null;
  marketingPaymentsPercentage: number | null;
  productVolume: number | null;
  measurementUnit: string | null;
  productVerticalLine: string | null;
  isVerticalLineBase: string | null;
  vlStrategy: string | null;
  vlGapPc: number | null;
  vlGapType: VlGapType;
  roundVerticalLine: string | null;
  targetMarginPc: number | null;
  status: string | null;
}

export interface AssortmentDirectoryParams {
  tradingId: number | null;
  internalCode: string | null;
  skuName: string | null;
  productLine: string | null;
  productVerticalLine: string | null;
  vlGapType?: VlGapType;
  ctmFilter?: boolean | null;
  kviFilter?: boolean | null;
  selectedProductVerticalLines?: string[] | null;
  selectedMinRetailPrices?: number[] | null;
  selectedMaxRetailPrices?: number[] | null;
  storeFormatIds: number[] | null;
  storeRegionIds: number[] | null;
  storeClusterIds: number[] | null;
  groupIds: number[] | null;
  categoryIds: number[] | null;
  subcategoryIds: number[] | null;
  page: number;
  size: number;
  sortField: string;
  sortDirection: string;
}

export interface AssortmentDirectoryFilterOptionsParams {
  selectedGroups: number[] | null;
  selectedCategories: number[] | null;
  selectedSubcategories: number[] | null;
  selectedStoreFormats: number[] | null;
  selectedStoreRegions: number[] | null;
  selectedStoreClusters: number[] | null;
  selectedVerticalLines?: string[] | null;
  selectedMinRetailPrices?: number[] | null;
  selectedMaxRetailPrices?: number[] | null;
  ctm?: boolean | null;
  kvi?: boolean | null;
}

export interface AssortmentDirectoryFilterOptions {
  storeFormats: ProductTag[],
  storeRegions: ProductTag[],
  storeClusters: ProductTag[],
  groups: ProductTag[],
  categories: ProductTag[],
  subcategories: ProductTag[],
  segments?: ProductTag[],
  productVerticalLineNames: string[],
  minRetailPrices: number[],
  maxRetailPrices: number[],
}

export interface ProductLineRecommendationSearchParams {
  tradingId: number | null;
  groupIds: number[] | null;
  categoryIds: number[] | null;
  subcategoryIds: number[] | null;
  nameThreshold: number;
  priceThreshold: number;
}

export interface ProductLineRecommendationSearchStatusId {
  taskId: number | null;
}

export interface UpdateActiveStatusParams {
  id: number;
  newStatus: boolean;
}

export interface FixedPriceSelectedItems {
  id: number;
  internalCode: string;
  skuName: string;
  storeFormat: string | null;
  storeRegion: string | null;
  storeCluster: string | null;
}

export interface CalcBySelectedSKUsParams {
  tradingId: number;
  isCommonCheckboxSelected: boolean;
  included: FixedPriceSelectedItems[];
  excludedIds: number[];
  filters: {
    storeFormatIds: number[];
    storeRegionIds: number[];
    storeClusterIds: number[];
    codeSalesPoint: string | null;
    skuName: string | null;
    internalCode: string | null;
    dateBefore?: string | null;
    dateAfter?: string | null;
    regularPriceStartDate?: string | null;
    regularPriceEndDate?: string | null;
    fileUploadDate?: string | null;
    regularPrices?: number[];
    uploadFileIdFilterValue?: number | null;
  };
}

export interface IProductsToLinkToCompetitor {
  internalCode: string;
  skuName: string;
  groupId: number;
  categoryId: number;
  subcategoryId: number | null;
  segmentId?: number;
  keyCharacteristicOne?: string;
  keyCharacteristicTwo?: string;
  recipeAttribute?: string;
  treatmentAttribute?: string;
  kvi?: number;
  ctm?: string;
}

export interface ILinkedProductsTableOptions {
  competitorCode: string[];
  group: IdAndName[];
  category: IdAndName[];
  subcategory: IdAndName[];
  internalCode: string[];
  skuName: string[];
  storeFormat: IdAndName[];
  storeRegion: IdAndName[];
  storeCluster: IdAndName[];
  keyCharacteristicOne: string[];
  keyCharacteristicTwo: string[];
  ctm: number | string[];
  kvi: number[];
}

export interface ILinkedProductsTableFilterValues {
  internalCodes?: string[];
  skuNames?: string[];
  categoryIds?: number[];
  subcategoryIds?: number[];
  groupIds?: number[];
  kviValue?: number;
  ctmValue?: string | number;
}

export interface ISelectCompetitorOptionsItem {
  label: string,
  value: string,
  competitorCode: string,
  competitorName: string,
  competitorFormat: IdAndName,
  competitorRegion: IdAndName,
  competitorCluster?: IdAndName,
  lastParsingSearchSite?: string,
}

export interface IProductSearchResult {
  name: string,
  score?: number,
  link: string,
}

export interface ICompetitorSelectionTask {
  productLinkId: number,
  taskStatus: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'ERROR',
  productName: string,
  errorMessage?: string,
}

export interface ICompetitorProduct {
  id: number,
  tradingId: number,
  competitorCode: string,
  competitorName: string,
  internalCode: string,
  skuName: string,
  keyCharacteristicOne?: string,
  keyCharacteristicTwo?: string,
  group: IdAndName,
  category: IdAndName,
  subcategory: IdAndName,
  storeFormat: IdAndName,
  storeRegion: IdAndName,
  storeCluster?: IdAndName | null,
  kvi?: number,
  parsingUrl?: string | null,
  productSearchResults: IProductSearchResult[],
  groupId?: number,
  categoryId?: number,
  subcategoryId?: number,
}

export interface IHttpUnlinkCompetitorProductsFilter {
  tradingId: number;
  competitorCodeList: string[];
  internalCodeList: string[];
  skuNameList?: string[];
  storeFormatIdList: number[];
  storeRegionIdList: number[];
  storeClusterIdList: number[];
  categoryIdList: number[];
  groupIdList: number[];
  lastParsingDateTimeFrom?: string | null;
  lastParsingDateTimeTo?: string | null;
  fetch?: string[];
}

export interface ICheckboxesStateWithFilter<FilterType = any> {
  filter: FilterType;
  selectCheckboxesData: {
    isCommonCheckboxSelected: boolean;
    includedIds: number[];
    excludedIds: number[];
  };
}

export interface IParsingConfig {
  id: number;
  tradingId: number;
  competitorCode: string;
  competitorName: string;
  competitorWebsiteUrl?: string | null;
  createdAt: string;
  startDateTime: string;
  endDateTime: string;
  inProcess: boolean;
  frequencyValue?: number;
  frequencyUnit?: "MINUTES" | "HOURS" | "DAYS" | "WEEKS" | "MONTHS";
  storeFormat: IdAndName;
  storeRegion: IdAndName;
  jobCompletedCount?: number;
  jobErrorCount?: number;
  competitorsParsingLinkCount?: number;
}

export interface IHttpFetchCompetitorLinkedProductsPayload {
  page: number;
  size: number;
  sortBy: string;
  sortDirection: string;
}

export interface IHttpFetchCompetitorLinkedProductsPayload {
  tradingId: number;
  competitorCodeList?: string[];
  internalCodeList?: string[];
  skuNameList?: string[];
  storeFormatIdList?: number[];
  storeRegionIdList?: number[];
  storeClusterIdList?: number[];
  categoryIdList?: number[];
  groupIdList?: number[];
  lastParsingDateTimeFrom?: string | null;
  lastParsingDateTimeTo?: string | null;
  fetch?: string[];
}

export interface ICompetitorSelected {
  competitorCode: string;
  competitorName: string;
  format: IdAndName;
  region: IdAndName;
  cluster?: IdAndName | null;
}

export interface IPaginationAndSortingData {
  page: number;
  size: number;
  sortBy: string;
  sortDirection: string;
}

export interface IExportTask {
  taskId: number;
  status: string;
  progress: number;
  createdAt: string;
  fileName?: string;
}

export interface ITokenForDownloadExportFile {
  token: string;
  expiry: string;
}

export interface IFetchAssortmentFilterOptionsParams {
  isSales?: boolean;
}

export interface IHttpFetchAssortmentFilterOptions {
  groups: IdAndName[];
  categories: IdAndName[];
  subcategories: IdAndName[];
  storeFormats: IdAndName[];
  storeRegions: IdAndName[];
  storeClusters: IdAndName[];
}

export interface ParsingResultsTableOptions {
  competitorCode: string[];
  competitorName: string[];
  file: IdAndName[];
  internalCode: string[];
  promoPrice: number[];
  regularPrice: number[];
  skuName: string[];
  storeCluster: IdAndName[];
  storeFormat: IdAndName[];
  storeRegion: IdAndName[];
  auditStatus: IdAndName[];
}

export interface PriceRange {
  min: number | null;
  max: number | null;
}

export interface ParsingResultsTableFilterValues {
  internalCodeList: string[];
  skuNameContainsList: string[];
  competitorCodeList: string[];
  competitorNameContainsList: string[];
  regularPrice: number[];
  promoPrices: number[];
  storeFormatIdList: number[];
  storeRegionIdList: number[];
  storeClusterIdList: number[];
  marketMedianDeviationRange: PriceRange;
  competitorMovingAvgDeviationRange: PriceRange;
  auditStatusList: string[];
  fileList: (string | number)[];
}

export interface AuditParams {
  marketMedianDeviation: number;
  competitorSlidingAverageDeviation: number;
  searchDepth: number;
}

export interface SortingFieldNames {
  fileName: string;
  auditStatus: string;
}

export interface FetchResultsFilterParams {
  tradingId: number;
  parsedFrom: string | null;
  parsedTo: string | null;
  competitorCodeList: string[];
  competitorNameContainsList: string[];
  internalCodeList: string[];
  skuNameContainsList: string[];
  promoPrices: number[];
  regularPrice: number[];
  marketMedianDeviationMin: number | null;
  marketMedianDeviationMax: number | null;
  competitorSlidingAverageDeviationMin: number | null;
  competitorSlidingAverageDeviationMax: number | null;
  auditResults: string[];
  fileIds: (string | number)[];
}

export interface StartPriceAuditRequest extends ICheckboxesStateWithFilter<FetchResultsFilterParams> {
  marketMedianDeviationPercentCap: number;
  competitorSlidingAverageDeviationPercentCap: number;
  auditDepthInDays: number;
}

export interface ParsingResultTableItem {
  id: number;
  parsedAt: string;
  competitorCode: string;
  competitorName: string;
  storeFormatId?: number;
  storeFormat: IdAndName;
  storeClusterId?: number;
  storeCluster: IdAndName;
  storeRegionId?: number;
  storeRegion: IdAndName;
  internalCode: string;
  skuName: string;
  regularPrice: number;
  promoPrice: number;
  parsingUrl: string;
  tradingId: number;
  parsingJob: any;
  fileName: string;
  fileId?: number;
  marketMedianDeviation?: number;
  competitorSlidingAverageDeviation?: number;
  auditResult?: string;
  marketMedianDeviationLimitExceeded?: boolean
  competitorSlidingAverageDeviationLimitExceeded?: boolean;
  isAuditInProgress?: boolean;
  auditStatus?: string;
}

export interface ILabelAndValue<T> {
  label: string;
  value: T;
}

export interface CartProductsTableFilterOptionsPayloadFilters {
  selectedGroups?: number[];
  selectedCategories?: number[];
  selectedSubcategories?: number[];
  selectedStoreFormats?: number[];
  selectedStoreRegions?: number[];
  selectedStoreClusters?: number[];
}

export interface UniqSkuData {
    id: number;
    taskId: number;
    internalCode: string;
}

export interface ApproveReportElementsSelectedCheckboxesData {
    isCommonCheckboxSelected: boolean;
    includedIds: number[];
    excludedIds: number[];
}

export interface IUpdateOmnichannelStrategiesTask {
  status: string;
  progress: number;
}
